<template>
  <div class="portal-root">

    <!-- ── LOGIN GATE ── -->
    <template v-if="!authenticated">
      <Navbar />
      <div class="login-screen">
        <div class="login-card">
          <img src="/images/logo.png" alt="Norland Advisory" class="login-logo" />
          <h2>Member Portal</h2>
          <p class="login-sub">Access your personalised portfolio dashboard</p>
          <form class="login-form" @submit.prevent="doLogin">
            <div class="field">
              <label>Email address</label>
              <input v-model.trim="loginForm.email" type="email" placeholder="EMAIL" autocomplete="email" required />
            </div>
            <div class="field">
              <label>Password</label>
              <input v-model="loginForm.password" type="password" placeholder="PASSWORD" autocomplete="current-password" required />
            </div>
            <p v-if="loginError" class="login-error">{{ loginError }}</p>
            <button type="submit" class="login-btn">Sign In</button>
          </form>
          <p class="demo-hint">Please contact norland advisory for access.</p>
          <a href="/images/Norland会员门户操作指南.pdf" class="login-guide-link" target="_blank" rel="noopener noreferrer">Member Portal User Guide</a>
        </div>
      </div>
      <SiteFooter />
    </template>

    <!-- ── DASHBOARD ── -->
    <div v-else class="dashboard">

      <!-- SIDEBAR -->
      <aside class="portal-sidebar" :class="{ 'is-collapsed': sidebarCollapsed }">
        <div class="sidebar-brand" @click="sidebarCollapsed = !sidebarCollapsed">
          <span class="brand-icon">P</span>
          <span v-if="!sidebarCollapsed" class="brand-text">Portfolio</span>
        </div>

        <nav class="sidebar-nav">
          <a v-for="tab in tabs" :key="tab.id" class="sidebar-link" :class="{ 'is-active': activeTab === tab.id }" @click="activeTab = tab.id">
            <i :class="tab.icon" class="sidebar-icon"></i>
            <span v-if="!sidebarCollapsed" class="sidebar-label">{{ tab.label }}</span>
          </a>
        </nav>

        <div class="sidebar-bottom">
          <router-link to="/" class="sidebar-link">
            <i class="el-icon-s-home sidebar-icon"></i>
            <span v-if="!sidebarCollapsed" class="sidebar-label">Back to Website</span>
          </router-link>
          <a class="sidebar-link" :class="{ 'is-active': activeTab === 'security' }" @click="goToPasswordSection">
            <i class="el-icon-lock sidebar-icon"></i>
            <span v-if="!sidebarCollapsed" class="sidebar-label">Settings</span>
          </a>
          <a class="sidebar-link" @click="doLogout">
            <i class="el-icon-switch-button sidebar-icon"></i>
            <span v-if="!sidebarCollapsed" class="sidebar-label">Logout</span>
          </a>
          <div v-if="!sidebarCollapsed" class="collapse-hint" @click="sidebarCollapsed = true">
            Collapse
          </div>
        </div>
      </aside>

      <!-- MAIN CONTENT -->
      <main class="portal-main">
        <header class="dash-header">
          <div>
            <h1 class="dash-title">Portfolio Dashboard - {{ user.nickname }}</h1>
            <p class="dash-updated">Last updated: {{ today }}</p>
          </div>
        </header>

        <!-- DASHBOARD TAB -->
        <div v-if="activeTab === 'dashboard'" class="content">

          <div class="welcome-banner">
            <div class="welcome-text">
              <p class="welcome-hi">Hi {{ user.nickname }},</p>
              <p class="welcome-lead">your portfolio has generated</p>
              <p class="welcome-amount">{{ formatCompactMoney(totalCapitalGain) }}</p>
              <p class="welcome-sub">in capital gains with Norland Team.</p>
            </div>
            <div class="welcome-badge" aria-hidden="true">
              <span class="welcome-badge-label">Total Capital Gain</span>
              <span class="welcome-badge-arrow">↗</span>
            </div>
          </div>

          <div class="balance-map-row">
            <section class="dash-section balance-map-col balance-sheet-col">
              <div class="sec-row">
                <h2 class="sec-heading">Balance Sheet</h2>
                <button class="sec-link" @click="goToIncomeTab">Update Profile</button>
              </div>
              <div class="balance-grid">
                <div v-for="card in balanceSheetCards" :key="card.label" class="balance-card" :class="card.cls">
                  <p class="b-label">{{ card.label }}</p>
                  <p class="b-value">{{ card.value }}</p>
                </div>
              </div>
            </section>

            <section class="dash-section balance-map-col portfolio-map-col">
              <div class="sec-row">
                <h2 class="sec-heading">Your Australia Portfolio Map</h2>
              </div>
              <div class="au-map-card">
                <div class="au-map-wrap">
                  <div v-if="mapAddresses.length" ref="portfolioBasemap" class="au-map-canvas" aria-label="Australia property basemap"></div>
                  <div v-else class="au-map-empty-inline">No property locations to display yet.</div>
                </div>
                <!-- 暂时先去掉好看一些 -->
                <!-- <div v-if="mapAddresses.length" class="au-map-addresses">
                  <span class="au-map-address-label">Mapped addresses:</span>
                  <span v-for="(address, idx) in mapAddresses" :key="'map-address-' + idx" class="au-map-address-chip">{{ address }}</span>
                </div> -->
              </div>
            </section>
          </div>

          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Properties ({{ properties.length }})</h2>
              <button class="sec-link" @click="activeTab = 'properties'">View all →</button>
            </div>
            <div class="prop-list prop-list-detailed">
              <article v-for="prop in currentProperties" :key="prop.id" class="prop-card prop-card-static prop-card-detailed">
                <div class="prop-head">
                  <span class="prop-badge" :class="prop.type === 'OWNER OCCUPIED' ? 'badge-oo' : 'badge-inv'">{{ prop.type }}</span>
                  <span class="prop-badge" :class="normalizeManagedTypeCode(prop.rawTypeCode) === 1 ? 'badge-norland' : 'badge-own'">{{ normalizeManagedTypeCode(prop.rawTypeCode) === 1 ? 'NORLAND' : 'OWN' }}</span>
                  <span v-if="prop.isSold" class="sold-badge">SOLD</span>
                  <span class="prop-address">{{ prop.address }}</span>
                  <button v-if="!prop.isSold" type="button" class="prop-update-btn" @click.stop="openEditPropertyDialog(prop)">Update Property</button>
                </div>
                <div class="prop-body">
                  <div class="prop-left">
                    <div class="prop-image" :aria-label="prop.address + ' image'">
                      <img v-if="prop.image" :src="prop.image" :alt="prop.address" />
                      <i v-else class="el-icon-house"></i>
                    </div>
                    <div class="prop-metrics">
                      <div v-for="m in prop.metrics" :key="m.label" class="pm">
                        <span class="pm-label">{{ m.label }}</span>
                        <span class="pm-value">{{ m.value }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="prop-right">
                    <template v-if="computeValuationChart(prop)">
                      <div class="prop-chart-card">
                        <div class="prop-chart-head">
                          <div>
                            <p class="prop-chart-title">Valuation Trend</p>
                            <p class="prop-chart-sub">{{ computeValuationChart(prop).startDate }} &rarr; {{ computeValuationChart(prop).endDate }}</p>
                          </div>
                          <span class="prop-chart-delta" :class="computeValuationChart(prop).isPositive ? 'pos' : 'neg'">
                            {{ computeValuationChart(prop).deltaText }}
                          </span>
                        </div>
                        <svg class="prop-chart-svg" :viewBox="computeValuationChart(prop).viewBox" preserveAspectRatio="xMidYMid meet" role="img" :aria-label="prop.address + ' valuation chart'">
                          <defs>
                            <linearGradient :id="'pchart-dash-' + prop.id" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stop-color="#2f7a6c" stop-opacity="0.32" />
                              <stop offset="100%" stop-color="#2f7a6c" stop-opacity="0" />
                            </linearGradient>
                          </defs>
                          <g>
                            <line :x1="computeValuationChart(prop).axisLeft" :y1="computeValuationChart(prop).baseY" :x2="computeValuationChart(prop).axisRight" :y2="computeValuationChart(prop).baseY" stroke="#cfded9" stroke-width="1" />
                            <line :x1="computeValuationChart(prop).axisLeft" y1="22" :x2="computeValuationChart(prop).axisLeft" :y2="computeValuationChart(prop).baseY" stroke="#cfded9" stroke-width="1" />
                            <line v-for="(yt, yi) in computeValuationChart(prop).yTicks" :key="'d-yt-' + yi" :x1="computeValuationChart(prop).axisLeft" :y1="yt.y" :x2="computeValuationChart(prop).axisRight" :y2="yt.y" stroke="#edf3f1" stroke-width="1" />
                            <text v-for="(yt, yi) in computeValuationChart(prop).yTicks" :key="'d-yl-' + yi" :x="computeValuationChart(prop).axisLeft - 4" :y="yt.y + 3" text-anchor="end" fill="#7b918b" font-size="9">{{ yt.label }}</text>
                            <text v-for="(xt, xi) in computeValuationChart(prop).xTicks" :key="'d-xl-' + xi" :x="xt.x" :y="computeValuationChart(prop).baseY + 14" :text-anchor="xt.anchor" fill="#7b918b" font-size="9">{{ xt.label }}</text>
                            <text :x="computeValuationChart(prop).axisLeft" y="14" fill="#7b918b" font-size="9">Valuation</text>
                            <text :x="(computeValuationChart(prop).axisLeft + computeValuationChart(prop).axisRight) / 2" :y="computeValuationChart(prop).baseY + 27" text-anchor="middle" fill="#7b918b" font-size="9">Time</text>
                          </g>
                          <path :d="computeValuationChart(prop).areaPath" :fill="'url(#pchart-dash-' + prop.id + ')'" />
                          <path :d="computeValuationChart(prop).linePath" fill="none" stroke="#1e7a5f" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
                          <circle v-for="(mp, mi) in computeValuationChart(prop).midPoints" :key="'mpd-' + mi" :cx="mp.x" :cy="mp.y" r="3" fill="#ffffff" stroke="#1e7a5f" stroke-width="1.6" />
                          <circle :cx="computeValuationChart(prop).startPoint.x" :cy="computeValuationChart(prop).startPoint.y" r="4" fill="#ffffff" stroke="#1e7a5f" stroke-width="2" />
                          <circle :cx="computeValuationChart(prop).endPoint.x" :cy="computeValuationChart(prop).endPoint.y" r="4.5" fill="#1e7a5f" stroke="#ffffff" stroke-width="2" />
                        </svg>
                        <div class="prop-chart-foot">
                          <div class="pcf-col">
                            <span class="pcf-label">Purchase</span>
                            <span class="pcf-value">{{ computeValuationChart(prop).startLabel }}</span>
                          </div>
                          <div class="pcf-col pcf-col-end">
                            <span class="pcf-label">{{ computeValuationChart(prop).endCaption }}</span>
                            <span class="pcf-value">{{ computeValuationChart(prop).endLabel }}</span>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="prop-chart-empty">No valuation data yet.</div>
                  </div>
                </div>
              </article>
            </div>
          </section>

          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Cash Flow</h2>
              <button class="sec-link" @click="activeTab = 'cashflow'">View details →</button>
            </div>
          </section>

        </div>

        <!-- PROPERTIES TAB -->
        <div v-if="activeTab === 'properties'" class="content">
          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Properties</h2>
              <button class="add-btn" @click="openCreatePropertyDialog">+ Add Property</button>
            </div>
            <p v-if="propertyActionError" class="profile-error">{{ propertyActionError }}</p>
            <div class="prop-list prop-list-detailed">
              <article v-for="prop in properties" :key="prop.id" class="prop-card prop-card-static prop-card-detailed">
                <div class="prop-head">
                  <span class="prop-badge" :class="prop.type === 'OWNER OCCUPIED' ? 'badge-oo' : 'badge-inv'">{{ prop.type }}</span>
                  <span class="prop-badge" :class="normalizeManagedTypeCode(prop.rawTypeCode) === 1 ? 'badge-norland' : 'badge-own'">{{ normalizeManagedTypeCode(prop.rawTypeCode) === 1 ? 'NORLAND' : 'OWN' }}</span>
                  <span v-if="prop.isSold" class="sold-badge">SOLD</span>
                  <span class="prop-address">{{ prop.address }}</span>
                  <div class="prop-actions">
                    <button v-if="!prop.isSold" type="button" class="prop-update-btn" @click.stop="openEditPropertyDialog(prop)">Update Property</button>
                    <button v-if="canDeleteProperty(prop)" type="button" class="prop-update-btn prop-delete-btn" :disabled="deletingPropertyId === prop.id" @click.stop="openDeletePropertyConfirm(prop)">
                      Delete
                    </button>
                  </div>
                </div>
                <div class="prop-body">
                  <div class="prop-left">
                    <div class="prop-image" :aria-label="prop.address + ' image'">
                      <img v-if="prop.image" :src="prop.image" :alt="prop.address" />
                      <i v-else class="el-icon-house"></i>
                    </div>
                    <div class="prop-metrics">
                      <div v-for="m in prop.metrics" :key="m.label" class="pm">
                        <span class="pm-label">{{ m.label }}</span>
                        <span class="pm-value">{{ m.value }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="prop-right">
                    <template v-if="computeValuationChart(prop)">
                      <div class="prop-chart-card">
                        <div class="prop-chart-head">
                          <div>
                            <p class="prop-chart-title">Valuation Trend</p>
                            <p class="prop-chart-sub">{{ computeValuationChart(prop).startDate }} &rarr; {{ computeValuationChart(prop).endDate }}</p>
                          </div>
                          <span class="prop-chart-delta" :class="computeValuationChart(prop).isPositive ? 'pos' : 'neg'">
                            {{ computeValuationChart(prop).deltaText }}
                          </span>
                        </div>
                        <svg class="prop-chart-svg" :viewBox="computeValuationChart(prop).viewBox" preserveAspectRatio="xMidYMid meet" role="img" :aria-label="prop.address + ' valuation chart'">
                          <defs>
                            <linearGradient :id="'pchart-' + prop.id" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stop-color="#2f7a6c" stop-opacity="0.32" />
                              <stop offset="100%" stop-color="#2f7a6c" stop-opacity="0" />
                            </linearGradient>
                          </defs>
                          <g>
                            <line :x1="computeValuationChart(prop).axisLeft" :y1="computeValuationChart(prop).baseY" :x2="computeValuationChart(prop).axisRight" :y2="computeValuationChart(prop).baseY" stroke="#cfded9" stroke-width="1" />
                            <line :x1="computeValuationChart(prop).axisLeft" y1="22" :x2="computeValuationChart(prop).axisLeft" :y2="computeValuationChart(prop).baseY" stroke="#cfded9" stroke-width="1" />
                            <line v-for="(yt, yi) in computeValuationChart(prop).yTicks" :key="'p-yt-' + yi" :x1="computeValuationChart(prop).axisLeft" :y1="yt.y" :x2="computeValuationChart(prop).axisRight" :y2="yt.y" stroke="#edf3f1" stroke-width="1" />
                            <text v-for="(yt, yi) in computeValuationChart(prop).yTicks" :key="'p-yl-' + yi" :x="computeValuationChart(prop).axisLeft - 4" :y="yt.y + 3" text-anchor="end" fill="#7b918b" font-size="9">{{ yt.label }}</text>
                            <text v-for="(xt, xi) in computeValuationChart(prop).xTicks" :key="'p-xl-' + xi" :x="xt.x" :y="computeValuationChart(prop).baseY + 14" :text-anchor="xt.anchor" fill="#7b918b" font-size="9">{{ xt.label }}</text>
                            <text :x="computeValuationChart(prop).axisLeft" y="14" fill="#7b918b" font-size="9">Valuation</text>
                            <text :x="(computeValuationChart(prop).axisLeft + computeValuationChart(prop).axisRight) / 2" :y="computeValuationChart(prop).baseY + 27" text-anchor="middle" fill="#7b918b" font-size="9">Time</text>
                          </g>
                          <path :d="computeValuationChart(prop).areaPath" :fill="'url(#pchart-' + prop.id + ')'" />
                          <path :d="computeValuationChart(prop).linePath" fill="none" stroke="#1e7a5f" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
                          <circle v-for="(mp, mi) in computeValuationChart(prop).midPoints" :key="'mpp-' + mi" :cx="mp.x" :cy="mp.y" r="3" fill="#ffffff" stroke="#1e7a5f" stroke-width="1.6" />
                          <circle :cx="computeValuationChart(prop).startPoint.x" :cy="computeValuationChart(prop).startPoint.y" r="4" fill="#ffffff" stroke="#1e7a5f" stroke-width="2" />
                          <circle :cx="computeValuationChart(prop).endPoint.x" :cy="computeValuationChart(prop).endPoint.y" r="4.5" fill="#1e7a5f" stroke="#ffffff" stroke-width="2" />
                        </svg>
                        <div class="prop-chart-foot">
                          <div class="pcf-col">
                            <span class="pcf-label">Purchase</span>
                            <span class="pcf-value">{{ computeValuationChart(prop).startLabel }}</span>
                          </div>
                          <div class="pcf-col pcf-col-end">
                            <span class="pcf-label">{{ computeValuationChart(prop).endCaption }}</span>
                            <span class="pcf-value">{{ computeValuationChart(prop).endLabel }}</span>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="prop-chart-empty">No valuation data yet.</div>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>

        <!-- PROFILE TAB -->
        <div v-if="activeTab === 'income'" class="content">
          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Financial Profile</h2>
            </div>

            <!-- INCOME -->
            <div class="fp-card">
              <div class="fp-card-header">
                <i class="el-icon-money fp-icon"></i>
                <span>Income <em class="fp-note">Annual, before tax (AUD)</em></span>
                <button class="fp-card-save-btn" :disabled="savingProfile" @click="saveMemberProfile">{{ savingProfile ? 'Saving...' : 'Save' }}</button>
              </div>
              <div class="fp-grid">
                <label class="fp-field">
                  <span>PayG / Salary</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.paygIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>ABN Income (Personal)</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.abnIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Company Income</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.companyIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Partner — PayG / Salary</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.partnerPaygIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Partner — ABN Income</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.partnerAbnIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>ETFs / Shares Distributions</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.etfDistributions" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Other Income</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.otherIncome" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
              </div>
              <div class="fp-total-row">
                <span>Total Annual Income</span>
                <span class="fp-total-val">{{ formatCurrency(formTotalAnnualIncome) }}</span>
              </div>
            </div>

            <!-- ASSETS -->
            <div class="fp-card">
              <div class="fp-card-header">
                <i class="el-icon-s-home fp-icon"></i>
                <span>Assets <em class="fp-note">Current value (AUD)</em></span>
                <button class="fp-card-save-btn" :disabled="savingProfile" @click="saveMemberProfile">{{ savingProfile ? 'Saving...' : 'Save' }}</button>
              </div>
              <div class="fp-grid">
                <label class="fp-field">
                  <span>Savings (Cash on Hand)</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.savings" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field fp-field--ro">
                  <span>Norland Properties <em class="fp-note">Auto-calculated</em></span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input :value="norlandPropertiesValue.toLocaleString('en-AU')" type="text" readonly /></div>
                </label>
                <label class="fp-field fp-field--ro">
                  <span>Other Properties <em class="fp-note">Auto-calculated</em></span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input :value="autoOtherPropertiesValue.toLocaleString('en-AU')" type="text" readonly /></div>
                </label>
                <label class="fp-field">
                  <span>ETFs / Shares / Crypto</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.etfsSharesCryptos" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Superannuation</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.superannuation" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Vehicles</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.vehicles" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Collections / Luxury Goods</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.collectionsLuxury" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Home Contents</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.homeContent" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Overseas Assets</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.overseasAssets" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Other Assets</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.otherAssets" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
              </div>
              <div class="fp-total-row">
                <span>Total Assets</span>
                <span class="fp-total-val">{{ formatCurrency(formTotalAssetsValue) }}</span>
              </div>
            </div>

            <!-- LIABILITIES -->
            <div class="fp-card">
              <div class="fp-card-header">
                <i class="el-icon-warning-outline fp-icon"></i>
                <span>Liabilities <em class="fp-note">Current balance (AUD)</em></span>
                <button class="fp-card-save-btn" :disabled="savingProfile" @click="saveMemberProfile">{{ savingProfile ? 'Saving...' : 'Save' }}</button>
              </div>
              <div class="fp-grid">
                <label class="fp-field">
                  <span>Home Loans <em class="fp-note">Auto-linked from owner occupied properties</em></span>
                  <div class="fp-wrap fp-wrap-ro"><span class="fp-pre">$</span><input :value="ownerOccupiedHomeLoans.toLocaleString('en-AU')" type="text" readonly /></div>
                </label>
                <label class="fp-field">
                  <span>Car Loans</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.carLoans" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Personal Loans</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.personalLoans" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>HECS / HELP Debt</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.hecsDebt" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Credit Card Owing</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.creditCardOwing" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
                <label class="fp-field">
                  <span>Other Liabilities</span>
                  <div class="fp-wrap"><span class="fp-pre">$</span><input v-model.number="profileForm.otherLiabilities" type="number" min="0" step="1" placeholder="0" /></div>
                </label>
              </div>
              <div v-if="currentProperties.length > 0" class="fp-sub-section">
                <p class="fp-sub-title">Investment Loans — {{ formatCurrency(formTotalInvestmentLoans) }}</p>
                <div class="fp-grid">
                  <div v-for="prop in currentProperties" :key="'loan-' + prop.id" class="fp-field fp-field-loan">
                    <span class="fp-prop-label">{{ prop.address }}</span>
                    <div class="fp-loan-row">
                      <div class="fp-wrap fp-wrap-ro">
                        <span class="fp-pre">$</span>
                        <input :value="toNumber(prop.rawLoan, 0).toLocaleString('en-AU')" type="text" readonly />
                      </div>
                      <button type="button" class="fp-loan-update-btn" @click="openEditPropertyDialog(prop)">Update</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="fp-total-row fp-total-row--red">
                <span>Total Liabilities</span>
                <span class="fp-total-val">{{ formatCurrency(formTotalLiabilitiesValue) }}</span>
              </div>
            </div>

            <p v-if="profileError" class="profile-error">{{ profileError }}</p>
            <div class="income-actions">
              <button class="income-save-btn" :disabled="savingProfile" @click="saveMemberProfile">{{ savingProfile ? 'Saving...' : 'Save Profile' }}</button>
            </div>
          </section>
        </div>

        <!-- SECURITY TAB -->
        <div v-if="activeTab === 'security'" class="content">
          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Security</h2>
            </div>
            <div class="fp-card">
              <div class="fp-card-header">
                <i class="el-icon-lock fp-icon"></i>
                <span>Update login password</span>
              </div>
              <div class="fp-grid security-form-grid">
                <label class="fp-field">
                  <span>Current Password</span>
                  <div class="fp-wrap"><input v-model="passwordForm.oldPassword" type="password" autocomplete="current-password" placeholder="Enter current password" /></div>
                </label>
                <label class="fp-field">
                  <span>New Password</span>
                  <div class="fp-wrap"><input v-model="passwordForm.newPassword" type="password" autocomplete="new-password" placeholder="At least 6 characters" /></div>
                </label>
                <label class="fp-field">
                  <span>Confirm New Password</span>
                  <div class="fp-wrap"><input v-model="passwordForm.confirmPassword" type="password" autocomplete="new-password" placeholder="Enter new password again" /></div>
                </label>
              </div>
              <div class="security-actions">
                <button class="fp-card-save-btn" :disabled="updatingPassword" @click="saveLoginPassword">{{ updatingPassword ? 'Updating...' : 'Update Password' }}</button>
              </div>
              <p v-if="passwordError" class="profile-error">{{ passwordError }}</p>
              <p v-if="passwordSuccess" class="profile-success">{{ passwordSuccess }}</p>
            </div>
          </section>
        </div>

        <!-- CASHFLOW TAB -->
        <div v-if="activeTab === 'cashflow'" class="content">
          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Cash Flow Summary (Monthly)</h2>
            </div>
            <p class="cashflow-note">-This summary only calculates personal based cash flow. Business incomes are excluded.</p>
            <div class="cashflow-card cashflow-card--wide">
              <div class="cashflow-summary-grid">
                <div v-for="column in cashFlowSummaryColumns" :key="column.title" class="cashflow-summary-column">
                  <div class="cashflow-summary-title">{{ column.title }}</div>
                  <div class="cashflow-summary-list">
                    <div v-for="row in column.rows" :key="column.title + '-' + row.label" class="cf-row" :class="{ 'cf-subtotal': row.subtotal, 'cf-final': row.final }">
                      <span>{{ row.label }}</span>
                      <span :class="row.amount < 0 ? 'cf-neg' : ((row.subtotal || row.final) && row.amount > 0 ? 'cf-pos' : '')">{{ row.display }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Per Property Cash Flow (Annual, Before Tax)</h2>
              <button class="add-btn" @click="openCreatePropertyDialog">+ Add Property</button>
            </div>
            <div v-if="!propertyAnnualCashFlows.length" class="cashflow-card cashflow-card--wide">
              <div class="cf-row">
                <span>No active properties yet.</span>
                <span>—</span>
              </div>
            </div>
            <div v-else class="cf-property-cards">
              <div v-for="item in propertyAnnualCashFlows" :key="'pcf-page-' + item.id" class="cashflow-card cf-property-card">
                <div class="cf-prop-head">
                  <div class="cf-prop-title">{{ item.address }}</div>
                  <button type="button" class="prop-update-btn cf-prop-update-btn" @click="openEditPropertyDialog(item.property)">Update</button>
                </div>
                <div class="cf-row">
                  <span>Annual rental income</span>
                  <span>{{ formatCurrency(item.annualRentalIncome) }}</span>
                </div>
                <div class="cf-row">
                  <span>Mortgage repayment</span>
                  <span>{{ formatCurrency(item.mortgageRepaymentAnnual) }}</span>
                </div>
                <div class="cf-row">
                  <span>Property management fee</span>
                  <span>{{ formatCurrency(item.propertyManagementFee) }}</span>
                </div>
                <div class="cf-row">
                  <span>Rental vacancy</span>
                  <span>{{ formatCurrency(item.rentalVacancy) }}</span>
                </div>
                <div class="cf-row">
                  <span>Council &amp; Water rate</span>
                  <span>{{ formatCurrency(item.councilAndWaterRates) }}</span>
                </div>
                <div class="cf-row">
                  <span>Building &amp; Landlord insurance</span>
                  <span>{{ formatCurrency(item.buildingAndLandlordsInsurance) }}</span>
                </div>
                <div class="cf-row">
                  <span>Maintenance</span>
                  <span>{{ formatCurrency(item.maintenanceCost) }}</span>
                </div>
                <div class="cf-row">
                  <span>Land tax</span>
                  <span>{{ formatCurrency(item.landTax) }}</span>
                </div>
                <div class="cf-row">
                  <span>Depreciation (Division 43)</span>
                  <span>{{ formatCurrency(item.depreciationDiv43) }}</span>
                </div>
                <div class="cf-row">
                  <span>Depreciation (Division 40)</span>
                  <span>{{ formatCurrency(item.depreciationDiv40) }}</span>
                </div>
                <div class="cf-row cf-subtotal">
                  <span>Annual cash flow (before tax)</span>
                  <span :class="item.annualCashFlowBeforeTax < 0 ? 'cf-neg' : 'cf-pos'">{{ formatCurrency(item.annualCashFlowBeforeTax) }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- PROJECTIONS TAB -->
        <div v-if="activeTab === 'projections'" class="content">
          <section class="dash-section">
            <h2 class="sec-heading">Projections</h2>
            <div class="soon-wrap">
              <i class="el-icon-data-analysis soon-icon"></i>
              <p>Portfolio growth projections — coming soon.</p>
            </div>
          </section>
        </div>

        <!-- RESEARCH TAB -->
        <div v-if="activeTab === 'research'" class="content">
          <section class="dash-section">
            <div class="sec-row">
              <h2 class="sec-heading">Research &amp; Whitepapers</h2>
              <select v-if="researchCategoryOptions.length > 1" v-model="selectedResearchCategory" class="research-category-select">
                <option value="all">All Categories</option>
                <option v-for="category in researchCategoryOptions" :key="'rcat-' + category" :value="category">{{ category }}</option>
              </select>
            </div>
            <div v-if="!researchDocs.length" class="soon-wrap">
              <i class="el-icon-document soon-icon"></i>
              <p>No documents available yet.</p>
            </div>
            <div v-else-if="!filteredResearchDocs.length" class="soon-wrap">
              <i class="el-icon-document soon-icon"></i>
              <p>No documents in this category yet.</p>
            </div>
            <div v-else class="research-doc-grid">
              <div v-for="doc in filteredResearchDocs" :key="'rdoc-' + doc.id" class="research-doc-card">
                <div class="research-doc-cover">
                  <img v-if="doc.image" :src="doc.image" :alt="doc.title" />
                  <i v-else class="el-icon-document research-doc-placeholder"></i>
                </div>
                <div class="research-doc-body">
                  <p v-if="doc.category" class="research-doc-category">{{ doc.category }}</p>
                  <h3 class="research-doc-title">{{ doc.title }}</h3>
                  <p v-if="doc.summary" class="research-doc-summary">{{ doc.summary }}</p>
                  <p v-if="doc.meta" class="research-doc-meta">{{ doc.meta }}</p>
                  <button v-if="doc.fileUrl" type="button" class="research-doc-view-btn" @click="viewingDoc = doc">View Document</button>
                  <span v-else class="research-doc-unavailable">Document not available</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- PDF VIEWER MODAL -->
        <div v-if="viewingDoc" class="doc-viewer-mask" @click.self="viewingDoc = null">
          <div class="doc-viewer-card">
            <div class="doc-viewer-header">
              <span class="doc-viewer-title">{{ viewingDoc.title }}</span>
              <button type="button" class="doc-viewer-close" @click="viewingDoc = null">✕</button>
            </div>
            <div class="doc-viewer-frame-wrap">
              <iframe class="doc-viewer-frame" :src="'https://docs.google.com/viewer?url=' + encodeURIComponent(viewingDoc.fileUrl) + '&embedded=true'" frameborder="0"></iframe>
              <div class="doc-viewer-popout-cover"></div>
            </div>
          </div>
        </div>

        <div v-if="showPropertyManagerDialog" class="dialog-mask">
          <div class="dialog-card property-dialog-card">
            <h3>{{ propertyForm.propIdNo ? 'Edit Property' : 'Add Property' }}</h3>
            <p class="dialog-sub">Fill in the property details below.</p>

            <div class="dialog-grid">
              <label class="dialog-field dialog-field-full">
                <span class="dialog-label">Address <span v-if="!isInvestmentOnlyUpdate" class="required-star">*</span></span>
                <input v-if="!isInvestmentOnlyUpdate" v-model.trim="propertyForm.location" type="text" class="dialog-input" placeholder="e.g. 12 Queen St, Melbourne VIC 3000" />
                <input v-else :value="propertyForm.location" type="text" class="dialog-input dialog-input-readonly" readonly />
              </label>
              <label class="dialog-field dialog-field-full" v-if="!isInvestmentOnlyUpdate">
                <span class="dialog-label">Property Type <span class="required-star">*</span></span>
                <select v-model="propertyForm.ownerType" class="dialog-input">
                  <option value="1">Investment</option>
                  <option value="10">Owner Occupied</option>
                </select>
              </label>
              <label class="dialog-field dialog-field-full">
                <span class="dialog-label">Holding Status</span>
                <select v-model="propertyForm.holdingStatus" class="dialog-input">
                  <option value="holding">Holding</option>
                  <option value="sold">Sold</option>
                </select>
              </label>
              <label class="dialog-field" v-if="!isInvestmentOnlyUpdate">
                <span class="dialog-label">Purchase Date <span class="required-star">*</span></span>
                <input v-model="propertyForm.purchaseDate" type="date" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="!isInvestmentOnlyUpdate">
                <span class="dialog-label">Purchase Price (AUD) <span class="required-star">*</span></span>
                <input v-model.number="propertyForm.purchasePrice" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="!isInvestmentOnlyUpdate && propertyForm.holdingStatus !== 'sold'">
                <span class="dialog-label">Valuation Date</span>
                <input v-model="propertyForm.valuationDate" type="date" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="!isInvestmentOnlyUpdate && propertyForm.holdingStatus !== 'sold'">
                <span class="dialog-label">Current Valuation (AUD)</span>
                <input v-model.number="propertyForm.valuation" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="propertyForm.holdingStatus === 'sold'">
                <span class="dialog-label">Sold Date</span>
                <input v-model="propertyForm.soldDate" type="date" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="propertyForm.holdingStatus === 'sold'">
                <span class="dialog-label">Sold Price (AUD)</span>
                <input v-model.number="propertyForm.soldPrice" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Rent per Week (AUD)</span>
                <input v-model.number="propertyForm.rentPw" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showLoanFields">
                <span class="dialog-label">Loan Amount (AUD)</span>
                <input v-model.number="propertyForm.loan" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showLoanFields">
                <span class="dialog-label">Interest Rate (%)</span>
                <input v-model.number="propertyForm.interestRate" type="number" min="0" step="0.01" class="dialog-input" placeholder="e.g. 6.50" />
              </label>
              <label class="dialog-field" v-if="showLoanFields">
                <span class="dialog-label">Loan Repayment Type</span>
                <select v-model.number="propertyForm.loanRepaymentType" class="dialog-input">
                  <option value="1">Principal & Interest</option>
                  <option value="10">Interest Only</option>
                </select>
              </label>
              <label class="dialog-field" v-if="showLoanFields">
                <span class="dialog-label">Monthly Loan Repayment (AUD)</span>
                <input :value="formatLoanRepaymentInput(calculatedLoanMonthlyRepayment)" type="text" class="dialog-input dialog-input-readonly" readonly />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Capital works / Division 43 (AUD/year)</span>
                <input v-model.number="propertyForm.depreciationDiv43" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Plant &amp; Equipment / Division 40 (AUD/year)</span>
                <input v-model.number="propertyForm.depreciationDiv40" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Rental Management Fee Rate (%)</span>
                <input v-model.number="propertyForm.rentalMgmtFeeRate" type="number" min="0" step="0.01" placeholder="e.g. 5.5" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Rental Vacancy Rate (%)</span>
                <input v-model.number="propertyForm.rentalVacancyRate" type="number" min="0" step="0.01" placeholder="e.g. 5.5" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Council & Water Rates (AUD/year)</span>
                <input v-model.number="propertyForm.councilAndWaterRates" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Building & Landlords Insurance (AUD/year)</span>
                <input v-model.number="propertyForm.buildingAndLandlordsInsurance" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Maintenance Cost (AUD/year)</span>
                <input v-model.number="propertyForm.maintenanceCost" type="number" min="0" step="1" class="dialog-input" />
              </label>
              <label class="dialog-field" v-if="showInvestmentFields">
                <span class="dialog-label">Land Tax (AUD/year)</span>
                <input v-model.number="propertyForm.landTax" type="number" min="0" step="1" class="dialog-input" />
              </label>
            </div>

            <p v-if="propertyManagerError" class="dialog-error">{{ propertyManagerError }}</p>

            <div class="dialog-actions">
              <button type="button" class="dialog-btn dialog-cancel" :disabled="propertyManagerBusy" @click="closePropertyManagerDialog">Cancel</button>
              <button type="button" class="dialog-btn dialog-confirm" :disabled="propertyManagerBusy" @click="submitPropertyForm">
                {{ propertyManagerBusy ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="showDeleteConfirmDialog" class="dialog-mask">
          <div class="dialog-card delete-confirm-card" role="dialog" aria-modal="true" aria-label="Confirm property deletion">
            <h3>Delete Property?</h3>
            <p class="dialog-sub">This action cannot be undone.</p>
            <div class="delete-summary-row">
              <span class="delete-summary-label">Property</span>
              <span class="delete-summary-value">{{ pendingDeleteProperty && pendingDeleteProperty.address ? pendingDeleteProperty.address : 'Selected property' }}</span>
            </div>
            <p v-if="propertyActionError" class="dialog-error">{{ propertyActionError }}</p>
            <div class="dialog-actions">
              <button type="button" class="dialog-btn dialog-cancel" :disabled="deletingPropertyId === (pendingDeleteProperty && pendingDeleteProperty.id)" @click="closeDeletePropertyConfirm">Cancel</button>
              <button type="button" class="dialog-btn dialog-danger" :disabled="deletingPropertyId === (pendingDeleteProperty && pendingDeleteProperty.id)" @click="confirmDeleteProperty">
                {{ deletingPropertyId === (pendingDeleteProperty && pendingDeleteProperty.id) ? 'Deleting...' : 'Delete Property' }}
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>

  </div>
</template>

<script>
import Navbar from '@/components/layout/Navbar.vue';
import SiteFooter from '@/components/layout/SiteFooter.vue';
import { userLogin, getUserInfo, storeSpecAction, pubStoreSpecAction, updateLoginPwd } from '@/api/apiService';
import { setToken } from '@/api/auth';
import env from '@/api/env';
import moment from 'moment';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
const storeId = env.storeId;

export default {
  name: 'MemberPortal',
  components: { Navbar, SiteFooter },
  created: function() {
    this.syncProfileForm();
    this.recalculateCashFlow();
    this.bootstrapAuthentication();
  },
  mounted: function() {
    if(this.authenticated) {
      this.handleAuthenticatedReady();
    }
  },
  beforeDestroy: function() {
    this.toggleBodyScrollLock(false);
    this.destroyBasemap();
  },
  watch: {
    activeTab: function(nextTab) {
      if(nextTab === 'dashboard') {
        this.$nextTick(() => {
          this.initBasemap();
          this.renderPropertyMarkers();
        });
      }
    },
    isAnyDialogOpen: function(isOpen) {
      this.toggleBodyScrollLock(isOpen);
    }
  },
  data: function() {
    return {
      authenticated: sessionStorage.getItem('portal_auth') === '1',
      sidebarCollapsed: false,
      activeTab: 'dashboard',
      researchDocs: [],
      selectedResearchCategory: 'all',
      viewingDoc: null,
      loginForm: { email: '', password: '' },
      loginError: '',
      tabs: [
        { id: 'dashboard', label: 'Dashboard', icon: 'el-icon-odometer' },
        { id: 'income', label: 'Profile', icon: 'el-icon-money' },
        { id: 'properties', label: 'Properties', icon: 'el-icon-office-building' },
        { id: 'cashflow', label: 'Cashflow', icon: 'el-icon-data-line' },
        { id: 'research', label: 'Research', icon: 'el-icon-document' }
      ],
      memberProfile: (function() {
        const defaults = {
          paygIncome: 0, abnIncome: 0, companyIncome: 0, partnerPaygIncome: 0, partnerAbnIncome: 0,
          etfDistributions: 0, otherIncome: 0,
          savings: 0, otherProperties: 0, etfsSharesCryptos: 0, superannuation: 0,
          vehicles: 0, collectionsLuxury: 0, homeContent: 0, overseasAssets: 0, otherAssets: 0,
          homeLoans: 0, propertyLoans: {}, carLoans: 0, personalLoans: 0,
          hecsDebt: 0, creditCardOwing: 0, otherLiabilities: 0,
          mortgageRepayments: 0
        };
        try {
          const cached = JSON.parse(sessionStorage.getItem('portal_profile') || '{}');
          return Object.assign({}, defaults, cached || {});
        } catch(e) {
          return defaults;
        }
      }()),
      profileForm: {
        paygIncome: 0, abnIncome: 0, companyIncome: 0, partnerPaygIncome: 0, partnerAbnIncome: 0,
        etfDistributions: 0, otherIncome: 0,
        savings: 0, otherProperties: 0, etfsSharesCryptos: 0, superannuation: 0,
        vehicles: 0, collectionsLuxury: 0, homeContent: 0, overseasAssets: 0, otherAssets: 0,
        homeLoans: 0, propertyLoans: {}, carLoans: 0, personalLoans: 0,
        hecsDebt: 0, creditCardOwing: 0, otherLiabilities: 0,
        mortgageRepayments: 0
      },
      profileError: '',
      savingProfile: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordError: '',
      passwordSuccess: '',
      updatingPassword: false,
      properties: [],
      currentProperties: [],
      basemap: null,
      basemapMarkersLayer: null,
      addressCoordsCache: {},
      geocodeQueue: Promise.resolve(),
      showPropertyManagerDialog: false,
      propertyManagerBusy: false,
      propertyManagerError: '',
      propertyActionError: '',
      deletingPropertyId: null,
      showDeleteConfirmDialog: false,
      pendingDeleteProperty: null,
      propertyForm: {
        propIdNo: null,
        serverTypeCode: null,
        location: '',
        ownerType: '1',
        holdingStatus: 'holding',
        purchasePrice: null,
        purchaseDate: '',
        valuation: null,
        valuationDate: '',
        soldPrice: null,
        soldDate: '',
        rentPw: null,
        loan: null,
        interestRate: null,
        loanRepaymentType: 1,
        loanMonthlyRepayment: null,
        rentalMgmtFeeRate: null,
        rentalVacancyRate: null,
        councilAndWaterRates: null,
        buildingAndLandlordsInsurance: null,
        maintenanceCost: null,
        landTax: null,
        depreciationDiv43: null,
        depreciationDiv40: null
      },
      cashFlow: [
        { label: 'Pre-tax Income', display: '$16,400', amount: 16400 },
        { label: 'Income Tax', display: '-$3,120', amount: -3120 },
        { label: 'After-tax Income', display: '$13,280', amount: 13280, subtotal: true },
        { label: 'Rental Income', display: '$5,740', amount: 5740 },
        { label: 'Mortgage Repayments', display: '-$9,840', amount: -9840 },
        { label: 'Property Expenses', display: '-$2,980', amount: -2980 },
        { label: 'Neg. Gearing Benefit', display: '$1,940', amount: 1940 },
        { label: 'Net Monthly Cash Flow', display: '$8,140', amount: 8140, final: true }
      ],
      user: (function() {
        try { return JSON.parse(sessionStorage.getItem('portal_user') || '{}'); } catch(e) { return {}; }
      }())
    };
  },
  computed: {
    today: function() {
      return new Date().toLocaleDateString('en-AU');
    },
    researchCategoryOptions: function() {
      const set = {};
      this.researchDocs.forEach((doc) => {
        const category = String((doc && doc.category) || '').trim();
        if(category) {
          set[category] = true;
        }
      });
      return Object.keys(set).sort((a, b) => a.localeCompare(b));
    },
    filteredResearchDocs: function() {
      if(this.selectedResearchCategory === 'all') {
        return this.researchDocs;
      }
      return this.researchDocs.filter((doc) => {
        return String((doc && doc.category) || '').trim() === this.selectedResearchCategory;
      });
    },
    totalCapitalGain: function() {
      return this.properties
        .filter((prop) => !this.canDeleteProperty(prop))
        .reduce((sum, prop) => {
          return sum + this.toNumber(prop.rawCapitalGain, 0);
        }, 0);
    },
    cashFlowSummaryColumns: function() {
      const f = this.memberProfile || {};
      const personalAnnualIncomeBeforeTax = this.toNumber(f.paygIncome, 0) + this.toNumber(f.abnIncome, 0);
      const partnerAnnualIncomeBeforeTax = this.toNumber(f.partnerPaygIncome, 0) + this.toNumber(f.partnerAbnIncome, 0);
      const otherPersonalAnnualIncomeBeforeTax = this.toNumber(f.etfDistributions, 0) + this.toNumber(f.otherIncome, 0);
      const companyAnnualIncomeBeforeTax = this.toNumber(f.companyIncome, 0);
      const combinedIncomeBeforeTaxAnnual = personalAnnualIncomeBeforeTax + partnerAnnualIncomeBeforeTax + otherPersonalAnnualIncomeBeforeTax + companyAnnualIncomeBeforeTax;

      const personalTaxableAnnual = personalAnnualIncomeBeforeTax + (partnerAnnualIncomeBeforeTax <= 0 ? otherPersonalAnnualIncomeBeforeTax : otherPersonalAnnualIncomeBeforeTax / 2);
      const partnerTaxableAnnual = partnerAnnualIncomeBeforeTax <= 0
        ? 0
        : partnerAnnualIncomeBeforeTax + (otherPersonalAnnualIncomeBeforeTax / 2);

      const personalTaxPayableAnnual = this.calculateAustralianIncomeTaxAnnual(personalTaxableAnnual);
      const partnerTaxPayableAnnual = partnerAnnualIncomeBeforeTax > 0 ? this.calculateAustralianIncomeTaxAnnual(partnerTaxableAnnual) : 0;
      const companyTaxPayableAnnual = Math.max(companyAnnualIncomeBeforeTax, 0) * 0.25;
      const combinedTaxPayableAnnual = personalTaxPayableAnnual + partnerTaxPayableAnnual + companyTaxPayableAnnual;
      const combinedIncomeAfterTaxAnnual = combinedIncomeBeforeTaxAnnual - combinedTaxPayableAnnual;

      const totalPropertyAnnualCashFlow = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.annualCashFlowBeforeTax, 0);
      }, 0);
      const eligibleNegativeGearingDeductionAnnual = this.propertyAnnualCashFlows.reduce((sum, item) => {
        if(!item || !item.property || this.isOwnerOccupiedProperty(item.property)) {
          return sum;
        }
        const annualCashFlow = this.toNumber(item.annualCashFlowBeforeTax, 0);
        const purchaseDate = item && item.property ? item.property.purchaseDate : '';
        const purchaseMoment = moment(purchaseDate);
        if(purchaseMoment.isValid() && !purchaseMoment.isBefore(moment('2026-07-01'))) {
          return sum;
        }
        const baseLoss = annualCashFlow < 0 ? Math.abs(annualCashFlow) : 0;
        const depreciationDeduction = this.toNumber(item.depreciationDiv43, 0) + this.toNumber(item.depreciationDiv40, 0);
        return sum + baseLoss + depreciationDeduction;
      }, 0);

      let negativeGearingBenefitAnnual = 0;
      if(partnerAnnualIncomeBeforeTax <= 0) {
        negativeGearingBenefitAnnual = Math.max(eligibleNegativeGearingDeductionAnnual * this.getNegativeGearingRate(personalAnnualIncomeBeforeTax), 0);
      } else {
        const splitDeduction = eligibleNegativeGearingDeductionAnnual / 2;
        const person1BenefitAnnual = splitDeduction * this.getNegativeGearingRate(personalAnnualIncomeBeforeTax);
        const person2BenefitAnnual = splitDeduction * this.getNegativeGearingRate(partnerAnnualIncomeBeforeTax);
        negativeGearingBenefitAnnual = Math.max(person1BenefitAnnual + person2BenefitAnnual, 0);
      }
      const negativeGearingBenefitMonthly = negativeGearingBenefitAnnual / 12;
      const totalPropertyMonthlyCashFlow = totalPropertyAnnualCashFlow / 12;
      const finalMonthlyCashFlow = combinedIncomeAfterTaxAnnual / 12 + totalPropertyMonthlyCashFlow + negativeGearingBenefitMonthly;
      const finalWeeklyCashFlow = finalMonthlyCashFlow * 12 / 52;
      const finalAnnualCashFlow = finalMonthlyCashFlow * 12;

      const formatMoneyRow = (label, value, extra) => Object.assign({
        label: label,
        amount: value,
        display: this.formatCurrency(value)
      }, extra || {});

      return [
        {
          title: 'Income (monthly)',
          rows: [
            formatMoneyRow('Personal income before tax', personalAnnualIncomeBeforeTax / 12),
            formatMoneyRow('Partner income before tax', partnerAnnualIncomeBeforeTax / 12),
            formatMoneyRow('Other personal income before tax', otherPersonalAnnualIncomeBeforeTax / 12),
            formatMoneyRow('Company income before tax', companyAnnualIncomeBeforeTax / 12),
            formatMoneyRow('Combined income before tax', combinedIncomeBeforeTaxAnnual / 12, { subtotal: true }),
            formatMoneyRow('Combined tax payable', combinedTaxPayableAnnual / 12),
            formatMoneyRow('Combined income after tax', combinedIncomeAfterTaxAnnual / 12, { final: true })
          ]
        },
        {
          title: 'Property cash flow (monthly)',
          rows: [
            formatMoneyRow('Rental income', this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.annualRentalIncome, 0), 0) / 12),
            formatMoneyRow('Mortgage repayment', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.mortgageRepaymentAnnual, 0), 0) / 12),
            formatMoneyRow('Property management fee', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.propertyManagementFee, 0), 0) / 12),
            formatMoneyRow('Rental vacancy', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.rentalVacancy, 0), 0) / 12),
            formatMoneyRow('Council & Water rate', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.councilAndWaterRates, 0), 0) / 12),
            formatMoneyRow('Building & Landlord insurance', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.buildingAndLandlordsInsurance, 0), 0) / 12),
            formatMoneyRow('Maintenance', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.maintenanceCost, 0), 0) / 12),
            formatMoneyRow('Land tax', -this.propertyAnnualCashFlows.reduce((sum, item) => sum + this.toNumber(item.landTax, 0), 0) / 12),
            formatMoneyRow('Combined property cash flow before tax', totalPropertyMonthlyCashFlow, { final: true })
          ]
        },
        {
          title: 'Final cash flow (monthly)',
          rows: [
            formatMoneyRow('Combined income before tax', combinedIncomeBeforeTaxAnnual / 12),
            formatMoneyRow('Combined tax payable', combinedTaxPayableAnnual / 12),
            formatMoneyRow('Combined property cash flow before tax', totalPropertyMonthlyCashFlow, { subtotal: true }),
            formatMoneyRow('Negative gearing', negativeGearingBenefitMonthly),
            formatMoneyRow('Final monthly cash flow', finalMonthlyCashFlow, { final: true }),
            formatMoneyRow('Final weekly cash flow', finalWeeklyCashFlow),
            formatMoneyRow('Final annual cash flow', finalAnnualCashFlow)
          ]
        }
      ];
    },
    norlandPropertiesValue: function() {
      return this.properties
        .filter((prop) => {
          const isNorlandProperty = this.normalizeManagedTypeCode(prop && prop.rawTypeCode) === 1;
          const soldDate = String((prop && prop.soldDate) || '').trim();
          return isNorlandProperty && !soldDate;
        })
        .reduce((sum, prop) => sum + this.toNumber(prop.rawValuation, 0), 0);
    },
    autoOtherPropertiesValue: function() {
      return this.properties
        .filter((prop) => {
          const isOtherProperty = this.normalizeManagedTypeCode(prop && prop.rawTypeCode) !== 1;
          const soldDate = String((prop && prop.soldDate) || '').trim();
          return isOtherProperty && !soldDate;
        })
        .reduce((sum, prop) => sum + this.toNumber(prop.rawValuation, 0), 0);
    },
    formTotalAnnualIncome: function() {
      const f = this.profileForm;
      return this.toNumber(f.paygIncome, 0) + this.toNumber(f.abnIncome, 0) + this.toNumber(f.companyIncome, 0) + this.toNumber(f.partnerPaygIncome, 0) + this.toNumber(f.partnerAbnIncome, 0) + this.toNumber(f.etfDistributions, 0) + this.toNumber(f.otherIncome, 0);
    },
    propertyAnnualCashFlows: function() {
      return this.currentProperties.map((prop) => {
        const isOwnerOccupied = this.isOwnerOccupiedProperty(prop);
        const defaults = isOwnerOccupied
          ? {
            rentalMgmtFeeRate: 0,
            rentalVacancyRate: 0,
            councilAndWaterRates: 0,
            buildingAndLandlordsInsurance: 0,
            maintenanceCost: 0,
            landTax: 0,
            interestRate: 6.5
          }
          : {
            rentalMgmtFeeRate: 12.5,
            rentalVacancyRate: 3,
            councilAndWaterRates: 3000,
            buildingAndLandlordsInsurance: 2000,
            maintenanceCost: 2000,
            landTax: 0,
            interestRate: 6.5
          };
        const annualRentalIncome = isOwnerOccupied ? 0 : (this.toNumber(prop.rawRentPw, 0) * 52);
        const loanAmount = this.toNumber(prop.rawLoan, 0);
        const interestRate = this.toNumber(prop.rawInterestRate, defaults.interestRate);
        const repaymentType = this.toNumber(prop.loanRepaymentType, 1);
        const monthlyRepayment = this.toNumber(prop.rawLoanMonthlyRepayment, NaN);
        const mortgageRepaymentAnnual = this.calculateMortgageRepaymentAnnual(loanAmount, interestRate, repaymentType, monthlyRepayment);

        const mgmtRate = this.toNumber(prop.rawRentalMgmtFeeRate, defaults.rentalMgmtFeeRate);
        const vacancyRate = this.toNumber(prop.rawRentalVacancyRate, defaults.rentalVacancyRate);

        const propertyManagementFee = annualRentalIncome * (mgmtRate / 100);
        const rentalVacancy = annualRentalIncome * (vacancyRate / 100);
        const councilAndWaterRates = this.toNumber(prop.rawCouncilAndWaterRates, defaults.councilAndWaterRates);
        const buildingAndLandlordsInsurance = this.toNumber(prop.rawBuildingAndLandlordsInsurance, defaults.buildingAndLandlordsInsurance);
        const maintenanceCost = this.toNumber(prop.rawMaintenanceCost, defaults.maintenanceCost);
        const landTax = this.toNumber(prop.rawLandTax, defaults.landTax);
        const depreciationDiv43 = isOwnerOccupied ? 0 : this.toNumber(prop.rawDepreciationDiv43, 0);
        const depreciationDiv40 = isOwnerOccupied ? 0 : this.toNumber(prop.rawDepreciationDiv40, 0);

        const annualExpense = mortgageRepaymentAnnual
          + propertyManagementFee
          + rentalVacancy
          + councilAndWaterRates
          + buildingAndLandlordsInsurance
          + maintenanceCost
          + landTax;

        return {
          id: prop.id,
          property: prop,
          address: prop.address || 'Property',
          annualRentalIncome: annualRentalIncome,
          mortgageRepaymentAnnual: mortgageRepaymentAnnual,
          propertyManagementFee: propertyManagementFee,
          rentalVacancy: rentalVacancy,
          councilAndWaterRates: councilAndWaterRates,
          buildingAndLandlordsInsurance: buildingAndLandlordsInsurance,
          maintenanceCost: maintenanceCost,
          landTax: landTax,
          depreciationDiv43: depreciationDiv43,
          depreciationDiv40: depreciationDiv40,
          annualExpense: annualExpense,
          annualCashFlowBeforeTax: annualRentalIncome - annualExpense
        };
      });
    },
    monthlyCashFlowRows: function() {
      const f = this.memberProfile || {};
      const personalAnnualIncomeBeforeTax = this.toNumber(f.paygIncome, 0) + this.toNumber(f.abnIncome, 0);
      const partnerAnnualIncomeBeforeTax = this.toNumber(f.partnerPaygIncome, 0) + this.toNumber(f.partnerAbnIncome, 0);
      const familyAnnualIncomeBeforeTax = personalAnnualIncomeBeforeTax + partnerAnnualIncomeBeforeTax;
      const negGearingCutoff = moment('2026-07-01');
      const totalPropertyAnnualCashFlow = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.annualCashFlowBeforeTax, 0);
      }, 0);
      const eligibleNegativeGearingDeductionAnnual = this.propertyAnnualCashFlows.reduce((sum, item) => {
        if(!item || !item.property || this.isOwnerOccupiedProperty(item.property)) {
          return sum;
        }
        const annualCashFlow = this.toNumber(item.annualCashFlowBeforeTax, 0);
        const purchaseDate = item && item.property ? item.property.purchaseDate : '';
        const purchaseMoment = moment(purchaseDate);
        // Only block deductions for properties explicitly purchased on/after cutoff.
        // Missing or invalid purchase dates remain eligible to avoid under-counting.
        if(purchaseMoment.isValid() && !purchaseMoment.isBefore(negGearingCutoff)) {
          return sum;
        }
        const baseLoss = annualCashFlow < 0 ? Math.abs(annualCashFlow) : 0;
        const depreciationDeduction = this.toNumber(item.depreciationDiv43, 0) + this.toNumber(item.depreciationDiv40, 0);
        return sum + baseLoss + depreciationDeduction;
      }, 0);

      const rows = [
        {
          label: 'Personal monthly income before tax',
          amount: personalAnnualIncomeBeforeTax / 12,
          display: this.formatCurrency(personalAnnualIncomeBeforeTax / 12)
        }
      ];

      if(partnerAnnualIncomeBeforeTax > 0) {
        rows.push({
          label: 'Partner monthly income before tax',
          amount: partnerAnnualIncomeBeforeTax / 12,
          display: this.formatCurrency(partnerAnnualIncomeBeforeTax / 12)
        });
      }

      rows.push({
        label: 'Family monthly income before tax',
        amount: familyAnnualIncomeBeforeTax / 12,
        display: this.formatCurrency(familyAnnualIncomeBeforeTax / 12),
        subtotal: true
      });

      const personalIncomeTaxAnnual = this.calculateAustralianIncomeTaxAnnual(personalAnnualIncomeBeforeTax);
      const partnerIncomeTaxAnnual = this.calculateAustralianIncomeTaxAnnual(partnerAnnualIncomeBeforeTax);
      const familyIncomeAfterTaxMonthly = (familyAnnualIncomeBeforeTax - personalIncomeTaxAnnual - partnerIncomeTaxAnnual) / 12;
      rows.push({
        label: 'Income After Tax',
        amount: familyIncomeAfterTaxMonthly,
        display: this.formatCurrency(familyIncomeAfterTaxMonthly),
        subtotal: true
      });

      const totalRentalIncomeMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.annualRentalIncome, 0);
      }, 0) / 12;
      const totalPropertyManagementFeeMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.propertyManagementFee, 0);
      }, 0) / 12;
      const totalRentalVacancyMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.rentalVacancy, 0);
      }, 0) / 12;
      const totalCouncilAndWaterMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.councilAndWaterRates, 0);
      }, 0) / 12;
      const totalInsuranceMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.buildingAndLandlordsInsurance, 0);
      }, 0) / 12;
      const totalMaintenanceMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.maintenanceCost, 0);
      }, 0) / 12;
      const totalLandTaxMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.landTax, 0);
      }, 0) / 12;
      const totalMortgageMonthly = this.propertyAnnualCashFlows.reduce((sum, item) => {
        return sum + this.toNumber(item.mortgageRepaymentAnnual, 0);
      }, 0) / 12;

      rows.push({
        label: 'Rental income',
        amount: totalRentalIncomeMonthly,
        display: this.formatCurrency(totalRentalIncomeMonthly)
      });
      rows.push({
        label: 'Property management fee',
        amount: -totalPropertyManagementFeeMonthly,
        display: this.formatCurrency(-totalPropertyManagementFeeMonthly)
      });
      rows.push({
        label: 'Rental vacancy',
        amount: -totalRentalVacancyMonthly,
        display: this.formatCurrency(-totalRentalVacancyMonthly)
      });
      rows.push({
        label: 'Council & Water rate',
        amount: -totalCouncilAndWaterMonthly,
        display: this.formatCurrency(-totalCouncilAndWaterMonthly)
      });
      rows.push({
        label: 'Building & Landlord insurance',
        amount: -totalInsuranceMonthly,
        display: this.formatCurrency(-totalInsuranceMonthly)
      });
      rows.push({
        label: 'Maintenance',
        amount: -totalMaintenanceMonthly,
        display: this.formatCurrency(-totalMaintenanceMonthly)
      });
      rows.push({
        label: 'Land tax',
        amount: -totalLandTaxMonthly,
        display: this.formatCurrency(-totalLandTaxMonthly)
      });
      rows.push({
        label: 'Mortgage repayment',
        amount: -totalMortgageMonthly,
        display: this.formatCurrency(-totalMortgageMonthly)
      });

      const totalPropertyMonthlyCashFlow = totalPropertyAnnualCashFlow / 12;
      rows.push({
        label: 'Total property cash flow before tax',
        amount: totalPropertyMonthlyCashFlow,
        display: this.formatCurrency(totalPropertyMonthlyCashFlow),
        subtotal: true
      });

      let negGearingBenefitAnnual = 0;

      if(partnerAnnualIncomeBeforeTax <= 0) {
        const negRate = this.getNegativeGearingRate(personalAnnualIncomeBeforeTax);
        negGearingBenefitAnnual = Math.max(eligibleNegativeGearingDeductionAnnual * negRate, 0);
        const monthlyNegativeGearing = -(negGearingBenefitAnnual / 12);
        const finalMonthlyCashFlow = familyIncomeAfterTaxMonthly + totalPropertyMonthlyCashFlow - monthlyNegativeGearing;
        rows.push({
          label: 'Negative Gearing',
          amount: monthlyNegativeGearing,
          display: this.formatCurrency(monthlyNegativeGearing)
        });
        rows.push({
          label: 'Monthly Cash Flow',
          amount: finalMonthlyCashFlow,
          display: this.formatCurrency(finalMonthlyCashFlow),
          final: true
        });
      } else {
        const splitDeduction = eligibleNegativeGearingDeductionAnnual / 2;
        const person1BenefitAnnual = splitDeduction * this.getNegativeGearingRate(personalAnnualIncomeBeforeTax);
        const person2BenefitAnnual = splitDeduction * this.getNegativeGearingRate(partnerAnnualIncomeBeforeTax);
        negGearingBenefitAnnual = Math.max(person1BenefitAnnual + person2BenefitAnnual, 0);
        const monthlyNegativeGearing = -(negGearingBenefitAnnual / 12);
        const netFamilyMonthly = familyIncomeAfterTaxMonthly + totalPropertyMonthlyCashFlow - monthlyNegativeGearing;
        rows.push({
          label: 'Negative Gearing',
          amount: monthlyNegativeGearing,
          display: this.formatCurrency(monthlyNegativeGearing)
        });
        rows.push({
          label: 'Monthly Cash Flow',
          amount: netFamilyMonthly,
          display: this.formatCurrency(netFamilyMonthly),
          final: true
        });
      }

      return rows;
    },
    formTotalInvestmentLoans: function() {
      return this.currentProperties.reduce((sum, prop) => {
        if(this.isOwnerOccupiedProperty(prop)) {
          return sum;
        }
        return sum + this.toNumber(prop.rawLoan, 0);
      }, 0);
    },
    ownerOccupiedHomeLoans: function() {
      return this.currentProperties.reduce((sum, prop) => {
        if(!this.isOwnerOccupiedProperty(prop)) {
          return sum;
        }
        return sum + this.toNumber(prop.rawLoan, 0);
      }, 0);
    },
    formTotalAssetsValue: function() {
      const f = this.profileForm;
      return this.norlandPropertiesValue + this.toNumber(f.savings, 0) + this.autoOtherPropertiesValue + this.toNumber(f.etfsSharesCryptos, 0) + this.toNumber(f.superannuation, 0) + this.toNumber(f.vehicles, 0) + this.toNumber(f.collectionsLuxury, 0) + this.toNumber(f.homeContent, 0) + this.toNumber(f.overseasAssets, 0) + this.toNumber(f.otherAssets, 0);
    },
    formTotalLiabilitiesValue: function() {
      const f = this.profileForm;
      return this.ownerOccupiedHomeLoans + this.formTotalInvestmentLoans + this.toNumber(f.carLoans, 0) + this.toNumber(f.personalLoans, 0) + this.toNumber(f.hecsDebt, 0) + this.toNumber(f.creditCardOwing, 0) + this.toNumber(f.otherLiabilities, 0);
    },
    balanceSheetCards: function() {
      const norlandProps = this.norlandPropertiesValue;
      const totalPropertyValue = norlandProps + this.autoOtherPropertiesValue;
      const savings = this.toNumber(this.memberProfile.savings, 0);
      const otherAssets = this.toNumber(this.memberProfile.etfsSharesCryptos, 0) + this.toNumber(this.memberProfile.superannuation, 0) + this.toNumber(this.memberProfile.vehicles, 0) + this.toNumber(this.memberProfile.collectionsLuxury, 0) + this.toNumber(this.memberProfile.homeContent, 0) + this.toNumber(this.memberProfile.overseasAssets, 0) + this.toNumber(this.memberProfile.otherAssets, 0);
      const totalAssets = totalPropertyValue + savings + otherAssets;
      const totalLiabilities = this.ownerOccupiedHomeLoans + this.formTotalInvestmentLoans + this.toNumber(this.memberProfile.carLoans, 0) + this.toNumber(this.memberProfile.personalLoans, 0) + this.toNumber(this.memberProfile.hecsDebt, 0) + this.toNumber(this.memberProfile.creditCardOwing, 0) + this.toNumber(this.memberProfile.otherLiabilities, 0);
      const netWorth = totalAssets - totalLiabilities;

      return [
        { label: 'TOTAL PROPERTY VALUE', value: this.formatCompactMoney(totalPropertyValue), cls: '' },
        { label: 'SAVINGS', value: this.formatCompactMoney(savings), cls: '' },
        { label: 'OTHER ASSETS', value: this.formatCompactMoney(otherAssets), cls: '' },
        { label: 'TOTAL ASSETS', value: this.formatCompactMoney(totalAssets), cls: 'card-highlight' },
        { label: 'TOTAL LIABILITIES', value: this.formatCompactMoney(totalLiabilities), cls: 'card-loans' },
        { label: 'NET WORTH', value: this.formatCompactMoney(netWorth), cls: 'card-networth' }
      ];
    },
    isInvestmentOnlyUpdate: function() {
      if(!this.propertyForm.propIdNo) {
        return false;
      }
      return this.toNumber(this.propertyForm.serverTypeCode, NaN) === 1;
    },
    showInvestmentFields: function() {
      if(this.propertyForm.holdingStatus === 'sold') {
        return false;
      }
      if(this.isInvestmentOnlyUpdate) {
        return true;
      }
      return this.normalizeOwnershipTypeCode(this.propertyForm.ownerType) === 1;
    },
    showLoanFields: function() {
      if(this.propertyForm.holdingStatus === 'sold') {
        return false;
      }
      if(this.isInvestmentOnlyUpdate) {
        return true;
      }
      const ownerType = this.normalizeOwnershipTypeCode(this.propertyForm.ownerType);
      return ownerType === 1 || ownerType === 10;
    },
    calculatedLoanMonthlyRepayment: function() {
      const loan = this.toNumber(this.propertyForm.loan, NaN);
      const rate = this.toNumber(this.propertyForm.interestRate, NaN);
      const repaymentType = this.toNumber(this.propertyForm.loanRepaymentType, 1);
      if(!(loan > 0) || !(rate >= 0)) {
        return this.toNumber(this.propertyForm.loanMonthlyRepayment, 0);
      }
      const annual = this.calculateMortgageRepaymentAnnual(loan, rate, repaymentType, NaN);
      return annual > 0 ? (annual / 12) : 0;
    },
    mapAddresses: function() {
      return this.properties
        .map((prop) => (prop && prop.address ? String(prop.address).trim() : ''))
        .filter((address) => !!address);
    },
    isAnyDialogOpen: function() {
      return !!(this.showPropertyManagerDialog || this.showDeleteConfirmDialog || this.viewingDoc);
    }
  },
  methods: {
    toggleBodyScrollLock: function(locked) {
      if(typeof document === 'undefined' || !document.body) {
        return;
      }
      document.body.style.overflow = locked ? 'hidden' : '';
    },
    bootstrapAuthentication: async function() {
      const tokenFromUrl = this.extractLoginTokenFromUrl();
      if(tokenFromUrl) {
        await this.loginWithAccessToken(tokenFromUrl);
        return;
      }

      if(this.authenticated) {
        const cachedToken = sessionStorage.getItem('portal_token') || '';
        if(cachedToken) {
          setToken(cachedToken);
        }
        await this.fetchMemberProfile();
        await this.fetchMyProperties();
      }
    },
    extractLoginTokenFromUrl: function() {
      const keys = ['token', 't', 'access_token', 'portal_token', 'portalToken'];

      if(this.$route && this.$route.query) {
        for(let i = 0; i < keys.length; i += 1) {
          const val = this.$route.query[keys[i]];
          if(typeof val === 'string' && val.trim()) {
            return val.trim();
          }
        }
      }

      try {
        const searchParams = new URLSearchParams(window.location.search || '');
        for(let i = 0; i < keys.length; i += 1) {
          const val = searchParams.get(keys[i]);
          if(val && String(val).trim()) {
            return String(val).trim();
          }
        }

        const hash = window.location.hash || '';
        const qIndex = hash.indexOf('?');
        if(qIndex >= 0) {
          const hashParams = new URLSearchParams(hash.slice(qIndex + 1));
          for(let i = 0; i < keys.length; i += 1) {
            const val = hashParams.get(keys[i]);
            if(val && String(val).trim()) {
              return String(val).trim();
            }
          }
        }
      } catch(_e) {
        // ignore url parse errors
      }

      return '';
    },
    removeLoginTokenFromUrl: function() {
      try {
        const keys = ['token', 't', 'access_token', 'portal_token', 'portalToken'];
        const url = new URL(window.location.href);
        let changed = false;

        keys.forEach((key) => {
          if(url.searchParams.has(key)) {
            url.searchParams.delete(key);
            changed = true;
          }
        });

        const hash = url.hash || '';
        const qIndex = hash.indexOf('?');
        if(qIndex >= 0) {
          const hashPath = hash.slice(0, qIndex);
          const hashParams = new URLSearchParams(hash.slice(qIndex + 1));
          keys.forEach((key) => {
            if(hashParams.has(key)) {
              hashParams.delete(key);
              changed = true;
            }
          });
          const hashQuery = hashParams.toString();
          url.hash = hashPath + (hashQuery ? ('?' + hashQuery) : '');
        }

        if(changed) {
          window.history.replaceState({}, document.title, url.toString());
        }
      } catch(_e) {
        // ignore url rewrite errors
      }
    },
    fetchResearchDocs: function() {
      return pubStoreSpecAction(storeId, 'pubListDocs').then((ret) => {
        const list = (ret && ret.lst) || [];
        if(Array.isArray(list)) {
          this.researchDocs = list.map((item) => ({
            category: (item && item.category && typeof item.category === 'object')
              ? (item.category.name || '')
              : (item.category || item.docType || ''),
            categoryId: (item && item.category && typeof item.category === 'object')
              ? (item.category.catId || '')
              : '',
            id: item.id || item._id,
            title: item.title || item.name || '',
            summary: item.desc || '',
            meta: (item.fileType || '') + (item.dateCreated ? ' | ' + new Date(item.dateCreated).toLocaleDateString() : ''),
            image: item.imageUrl || '',
            fileUrl: item.fileUrl || item.url || item.downloadUrl || item.link || ''
          }));
          if(this.selectedResearchCategory !== 'all') {
            const exists = this.researchDocs.some((doc) => String((doc && doc.category) || '').trim() === this.selectedResearchCategory);
            if(!exists) {
              this.selectedResearchCategory = 'all';
            }
          }
        }
      }).catch(() => { });
    },
    handleAuthenticatedReady: function() {
      this.fetchResearchDocs();
      this.$nextTick(() => {
        this.initBasemap();
        this.renderPropertyMarkers();
      });
    },
    loginWithAccessToken: async function(token) {
      const safeToken = String(token || '').trim();
      this.removeLoginTokenFromUrl();
      if(!safeToken) {
        return;
      }

      this.loginError = '';
      this.authenticated = true;
      sessionStorage.setItem('portal_auth', '1');
      sessionStorage.setItem('portal_token', safeToken);
      setToken(safeToken);

      try {
        const info = await getUserInfo();
        const authInfo = info && typeof info === 'object' ? info : {};
        const refreshedToken = authInfo && authInfo.token ? String(authInfo.token) : safeToken;
        const refreshedUser = authInfo && authInfo.user
          ? authInfo.user
          : authInfo;

        sessionStorage.setItem('portal_auth', '1');
        sessionStorage.setItem('portal_token', refreshedToken);
        setToken(refreshedToken);

        this.user = refreshedUser && typeof refreshedUser === 'object' ? refreshedUser : {};
        sessionStorage.setItem('portal_user', JSON.stringify(this.user));

        await this.fetchMemberProfile();
        await this.fetchMyProperties();
        this.handleAuthenticatedReady();
      } catch(error) {
        this.authenticated = false;
        this.user = {};
        sessionStorage.removeItem('portal_auth');
        sessionStorage.removeItem('portal_user');
        sessionStorage.removeItem('portal_token');
        sessionStorage.removeItem('portal_profile');
        setToken('');
        const msg = error && error.message ? error.message : '';
        this.loginError = msg || 'Token login failed. Please sign in manually.';
      }
    },
    initBasemap: function() {
      const mapHost = this.$refs.portfolioBasemap;
      if(!mapHost) {
        return;
      }

      if(this.basemap && this.basemap._container !== mapHost) {
        this.destroyBasemap();
      }

      if(!this.basemap) {
        this.basemap = L.map(mapHost, {
          center: [-25.2744, 133.7751],
          zoom: 3,
          minZoom: 3,
          maxZoom: 8,
          zoomControl: true,
          attributionControl: true
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors'
        }).addTo(this.basemap);
      }

      this.basemap.setView([-25.2744, 133.7751], 3);
      this.basemap.invalidateSize();
    },
    destroyBasemap: function() {
      if(this.basemapMarkersLayer) {
        this.basemapMarkersLayer.remove();
        this.basemapMarkersLayer = null;
      }
      if(this.basemap) {
        this.basemap.remove();
        this.basemap = null;
      }
    },
    renderPropertyMarkers: function() {
      if(!this.mapAddresses.length) {
        if(this.basemapMarkersLayer) {
          this.basemapMarkersLayer.clearLayers();
        }
        return;
      }

      this.initBasemap();
      if(!this.basemap) {
        return;
      }

      if(this.basemapMarkersLayer) {
        this.basemapMarkersLayer.remove();
      }
      this.basemapMarkersLayer = L.layerGroup().addTo(this.basemap);

      this.loadAddressCoordsCache();

      const props = this.properties.filter((prop) => !!(prop && prop.address));
      const allCoords = [];

      props.forEach((prop) => {
        this.resolveAddressCoord(prop.address).then((coord) => {
          if(!coord || !this.basemapMarkersLayer) { return; }
          const markerIcon = L.divIcon({
            className: 'map-flag-icon-wrap',
            html: '<span class="map-flag-pole"></span><span class="map-flag-pennant"></span>',
            iconSize: [20, 26],
            iconAnchor: [10, 24],
            popupAnchor: [0, -20],
            tooltipAnchor: [0, -20]
          });
          const marker = L.marker([coord.lat, coord.lng], { icon: markerIcon })
            .bindTooltip(prop.isSold ? (prop.address + ' (SOLD)') : prop.address, { direction: 'top', offset: [0, -10] })
            .addTo(this.basemapMarkersLayer);

          if(marker && marker.getElement && marker.getElement()) {
            marker.getElement().classList.add('map-flag-marker');
            if(prop.isSold) {
              marker.getElement().classList.add('is-sold');
            }
          }

          allCoords.push([coord.lat, coord.lng]);
          if(allCoords.length === props.length && allCoords.length > 1 && this.basemap) {
            this.basemap.fitBounds(L.latLngBounds(allCoords), { padding: [40, 40], maxZoom: 11 });
          }
        });
      });
    },
    loadAddressCoordsCache: function() {
      if(this.addressCoordsCache && Object.keys(this.addressCoordsCache).length) { return; }
      try {
        const raw = sessionStorage.getItem('portal_addr_coords_v2');
        this.addressCoordsCache = raw ? JSON.parse(raw) || {} : {};
      } catch(_e) {
        this.addressCoordsCache = {};
      }
    },
    persistAddressCoordsCache: function() {
      try {
        sessionStorage.setItem('portal_addr_coords_v2', JSON.stringify(this.addressCoordsCache || {}));
      } catch(_e) { /* ignore */ }
    },
    resolveAddressCoord: function(address) {
      const key = String(address || '').trim();
      if(!key) { return Promise.resolve(null); }
      const cached = this.addressCoordsCache[key];
      if(cached && Number.isFinite(cached.lat) && Number.isFinite(cached.lng)) {
        return Promise.resolve(cached);
      }
      this.geocodeQueue = this.geocodeQueue.then(() => {
        const hit = this.addressCoordsCache[key];
        if(hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) {
          return hit;
        }
        return this.geocodeAddress(key).then((coord) => {
          if(coord) {
            this.$set(this.addressCoordsCache, key, coord);
            this.persistAddressCoordsCache();
          }
          return new Promise((resolve) => setTimeout(() => resolve(coord), 1100));
        });
      });
      return this.geocodeQueue.then(() => this.addressCoordsCache[key] || this.fallbackStateCoord(key));
    },
    geocodeAddress: function(address) {
      const variants = this.buildGeocodeVariants(address);
      const tryNext = (idx) => {
        if(idx >= variants.length) { return Promise.resolve(null); }
        const url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=au&q=' + encodeURIComponent(variants[idx]);
        return fetch(url, { headers: { 'Accept': 'application/json' } })
          .then((res) => res.ok ? res.json() : [])
          .then((list) => {
            if(Array.isArray(list) && list.length) {
              const lat = parseFloat(list[0].lat);
              const lng = parseFloat(list[0].lon);
              if(Number.isFinite(lat) && Number.isFinite(lng)) {
                return { lat: lat, lng: lng };
              }
            }
            return idx + 1 < variants.length
              ? new Promise((resolve) => setTimeout(() => resolve(tryNext(idx + 1)), 1100))
              : null;
          })
          .catch(() => idx + 1 < variants.length
            ? new Promise((resolve) => setTimeout(() => resolve(tryNext(idx + 1)), 1100))
            : null);
      };
      return tryNext(0);
    },
    buildGeocodeVariants: function(address) {
      const raw = String(address || '').trim().replace(/\s+/g, ' ');
      if(!raw) { return []; }
      const variants = [raw];
      // 73A Assen Street -> 73 Assen Street (drop unit suffix letter)
      const noUnitLetter = raw.replace(/^(\d+)[A-Za-z]\b/, '$1');
      if(noUnitLetter !== raw) { variants.push(noUnitLetter); }
      // Drop leading number entirely: "Assen Street Utakarra WA 6530"
      const noNumber = raw.replace(/^\d+[A-Za-z]?\s+/, '');
      if(noNumber && noNumber !== raw && variants.indexOf(noNumber) === -1) {
        variants.push(noNumber);
      }
      // Suburb + state + postcode tail (last 3 tokens incl. postcode)
      const tokens = raw.split(/\s+/);
      if(tokens.length >= 3) {
        const tail = tokens.slice(-3).join(' ');
        if(variants.indexOf(tail) === -1) { variants.push(tail); }
      }
      return variants;
    },
    fallbackStateCoord: function(address) {
      const state = this.inferAustralianState(address);
      return this.australiaStateCoordinates(state);
    },
    goToIncomeTab: function() {
      this.activeTab = 'income';
    },
    goToPasswordSection: function() {
      this.activeTab = 'security';
    },
    doLogin: async function() {
      this.loginError = '';
      try {
        const ret = await userLogin({ storeId, username: this.loginForm.email, password: this.loginForm.password });
        this.authenticated = true;
        this.user = ret && ret.user ? ret.user : {};
        sessionStorage.setItem('portal_auth', '1');
        sessionStorage.setItem('portal_user', JSON.stringify(this.user));
        sessionStorage.setItem('portal_token', ret && ret.token ? ret.token : '');
        setToken(ret && ret.token ? ret.token : '');
        await this.fetchMemberProfile();
        await this.fetchMyProperties();
        this.handleAuthenticatedReady();
      } catch(err) {
        const msg = err && err.message ? err.message : '';
        this.loginError = msg || 'Invalid email or password. Please try again.';
      }
    },
    fetchMemberProfile: async function() {
      try {
        const ret = await storeSpecAction(storeId, 'memGetMyProfile', {});
        const raw = ret && typeof ret === 'object'
          ? (ret.memberProfile || ret.profile || ret.data || ret)
          : {};
        this.memberProfile = this.normalizeMemberProfile(raw);
        sessionStorage.setItem('portal_profile', JSON.stringify(this.memberProfile));
        this.syncProfileForm();
        this.recalculateCashFlow();
      } catch(error) {
        console.error('Failed to load member profile by storeSpecAction:', error);
      }
    },
    normalizeMemberProfile: function(source) {
      const raw = source && typeof source === 'object' ? source : {};
      const cur = this.memberProfile && typeof this.memberProfile === 'object' ? this.memberProfile : {};
      const n = (keys, fb) => this.toNumber(this.pick(raw, keys), this.toNumber(cur[keys[0]], fb));
      return {
        paygIncome: n(['paygIncome', 'salaryIncome'], 0),
        abnIncome: n(['abnIncome', 'businessIncome'], 0),
        companyIncome: n(['companyIncome', 'companyBusinessIncome'], 0),
        partnerPaygIncome: n(['partnerPaygIncome', 'partnerSalary'], 0),
        partnerAbnIncome: n(['partnerAbnIncome', 'partnerBusiness'], 0),
        etfDistributions: n(['etfDistributions', 'shareDistributions'], 0),
        otherIncome: n(['otherIncome'], 0),
        savings: n(['savings', 'cashOnHand', 'cashBalance'], 0),
        otherProperties: n(['otherProperties'], 0),
        etfsSharesCryptos: n(['etfsSharesCryptos', 'shares'], 0),
        superannuation: n(['superannuation', 'super'], 0),
        vehicles: n(['vehicles'], 0),
        collectionsLuxury: n(['collectionsLuxury', 'collections'], 0),
        homeContent: n(['homeContent'], 0),
        overseasAssets: n(['overseasAssets'], 0),
        otherAssets: n(['otherAssets'], 0),
        homeLoans: n(['homeLoans'], 0),
        propertyLoans: (raw.propertyLoans && typeof raw.propertyLoans === 'object') ? raw.propertyLoans : (cur.propertyLoans || {}),
        carLoans: n(['carLoans'], 0),
        personalLoans: n(['personalLoans'], 0),
        hecsDebt: n(['hecsDebt', 'hecs', 'helpDebt'], 0),
        creditCardOwing: n(['creditCardOwing', 'creditCard'], 0),
        otherLiabilities: n(['otherLiabilities'], 0),
        mortgageRepayments: n(['mortgageRepayments', 'monthlyMortgageRepayments', 'mortgagePerMonth'], 0)
      };
    },
    syncProfileForm: function() {
      const m = this.memberProfile;
      const loans = {};
      this.currentProperties.forEach(function(p) {
        const mapped = this.toNumber(p.rawLoan, NaN);
        loans[p.id] = Number.isFinite(mapped) ? mapped : this.toNumber((m.propertyLoans || {})[p.id], 0);
      }, this);
      this.profileForm = {
        paygIncome: this.toNumber(m.paygIncome, 0),
        abnIncome: this.toNumber(m.abnIncome, 0),
        companyIncome: this.toNumber(m.companyIncome, 0),
        partnerPaygIncome: this.toNumber(m.partnerPaygIncome, 0),
        partnerAbnIncome: this.toNumber(m.partnerAbnIncome, 0),
        etfDistributions: this.toNumber(m.etfDistributions, 0),
        otherIncome: this.toNumber(m.otherIncome, 0),
        savings: this.toNumber(m.savings, 0),
        otherProperties: this.autoOtherPropertiesValue,
        etfsSharesCryptos: this.toNumber(m.etfsSharesCryptos, 0),
        superannuation: this.toNumber(m.superannuation, 0),
        vehicles: this.toNumber(m.vehicles, 0),
        collectionsLuxury: this.toNumber(m.collectionsLuxury, 0),
        homeContent: this.toNumber(m.homeContent, 0),
        overseasAssets: this.toNumber(m.overseasAssets, 0),
        otherAssets: this.toNumber(m.otherAssets, 0),
        homeLoans: this.ownerOccupiedHomeLoans,
        propertyLoans: loans,
        carLoans: this.toNumber(m.carLoans, 0),
        personalLoans: this.toNumber(m.personalLoans, 0),
        hecsDebt: this.toNumber(m.hecsDebt, 0),
        creditCardOwing: this.toNumber(m.creditCardOwing, 0),
        otherLiabilities: this.toNumber(m.otherLiabilities, 0),
        mortgageRepayments: this.toNumber(m.mortgageRepayments, 0)
      };
    },
    saveMemberProfile: async function() {
      this.profileError = '';
      this.savingProfile = true;
      const f = this.profileForm;
      const nextProfile = {
        paygIncome: this.toNumber(f.paygIncome, 0),
        abnIncome: this.toNumber(f.abnIncome, 0),
        companyIncome: this.toNumber(f.companyIncome, 0),
        partnerPaygIncome: this.toNumber(f.partnerPaygIncome, 0),
        partnerAbnIncome: this.toNumber(f.partnerAbnIncome, 0),
        etfDistributions: this.toNumber(f.etfDistributions, 0),
        otherIncome: this.toNumber(f.otherIncome, 0),
        savings: this.toNumber(f.savings, 0),
        otherProperties: this.autoOtherPropertiesValue,
        etfsSharesCryptos: this.toNumber(f.etfsSharesCryptos, 0),
        superannuation: this.toNumber(f.superannuation, 0),
        vehicles: this.toNumber(f.vehicles, 0),
        collectionsLuxury: this.toNumber(f.collectionsLuxury, 0),
        homeContent: this.toNumber(f.homeContent, 0),
        overseasAssets: this.toNumber(f.overseasAssets, 0),
        otherAssets: this.toNumber(f.otherAssets, 0),
        homeLoans: this.ownerOccupiedHomeLoans,
        propertyLoans: Object.assign({}, f.propertyLoans || {}),
        carLoans: this.toNumber(f.carLoans, 0),
        personalLoans: this.toNumber(f.personalLoans, 0),
        hecsDebt: this.toNumber(f.hecsDebt, 0),
        creditCardOwing: this.toNumber(f.creditCardOwing, 0),
        otherLiabilities: this.toNumber(f.otherLiabilities, 0),
        mortgageRepayments: this.toNumber(f.mortgageRepayments, 0)
      };
      try {
        await storeSpecAction(storeId, 'memUpdateMyProfile', nextProfile);
        this.memberProfile = Object.assign({}, this.memberProfile, nextProfile);
        sessionStorage.setItem('portal_profile', JSON.stringify(this.memberProfile));
        this.recalculateCashFlow();
      } catch(error) {
        const msg = error && error.message ? error.message : '';
        this.profileError = msg || 'Failed to save profile.';
      } finally {
        this.savingProfile = false;
      }
    },
    saveLoginPassword: async function() {
      this.passwordError = '';
      this.passwordSuccess = '';

      const oldPassword = String(this.passwordForm.oldPassword || '');
      const newPassword = String(this.passwordForm.newPassword || '');
      const confirmPassword = String(this.passwordForm.confirmPassword || '');

      if(!oldPassword || !newPassword || !confirmPassword) {
        this.passwordError = 'Please fill in current password and new password.';
        return;
      }
      if(newPassword.length < 6) {
        this.passwordError = 'New password must be at least 6 characters.';
        return;
      }
      if(newPassword !== confirmPassword) {
        this.passwordError = 'The new passwords do not match.';
        return;
      }
      if(oldPassword === newPassword) {
        this.passwordError = 'New password must be different from current password.';
        return;
      }

      this.updatingPassword = true;
      try {
        await updateLoginPwd({ oldPassword: oldPassword, newPassword: newPassword });
        this.passwordForm = {
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        };
        const successMsg = 'Password updated successfully.';
        if(this.$message && typeof this.$message.success === 'function') {
          this.$message.success(successMsg);
        } else {
          this.passwordSuccess = successMsg;
        }
      } catch(error) {
        const msg = error && error.message ? error.message : '';
        this.passwordError = msg || 'Failed to update password.';
      } finally {
        this.updatingPassword = false;
      }
    },
    recalculateCashFlow: function() {
      this.cashFlow = this.monthlyCashFlowRows.slice();
    },
    calculateMortgageRepaymentAnnual: function(loanAmount, interestRate, repaymentType, monthlyRepayment) {
      const loan = this.toNumber(loanAmount, 0);
      if(Number.isFinite(monthlyRepayment) && monthlyRepayment > 0) {
        return monthlyRepayment * 12;
      }
      if(loan <= 0) {
        return 0;
      }

      const annualRate = Math.max(this.toNumber(interestRate, 0), 0) / 100;
      if(annualRate <= 0) {
        return 0;
      }

      const isInterestOnly = repaymentType === 10 || repaymentType === '10' || repaymentType === 2 || repaymentType === '2';
      if(isInterestOnly) {
        return loan * annualRate;
      }

      const monthlyRate = annualRate / 12;
      const n = 30 * 12;
      if(monthlyRate <= 0) {
        return loan / 30;
      }
      const factor = Math.pow(1 + monthlyRate, n);
      const monthlyPI = loan * monthlyRate * factor / (factor - 1);
      return Number.isFinite(monthlyPI) ? monthlyPI * 12 : 0;
    },
    calculateAustralianIncomeTaxAnnual: function(annualIncomeRaw) {
      const annualIncome = Math.max(this.toNumber(annualIncomeRaw, 0), 0);
      if(annualIncome <= 0) {
        return 0;
      }

      let annualTax = 0;
      if(annualIncome <= 18200) {
        annualTax = 0;
      } else if(annualIncome <= 45000) {
        annualTax = (annualIncome - 18200) * 0.16;
      } else if(annualIncome <= 135000) {
        annualTax = 4288 + (annualIncome - 45000) * 0.30;
      } else if(annualIncome <= 190000) {
        annualTax = 31288 + (annualIncome - 135000) * 0.37;
      } else {
        annualTax = 51638 + (annualIncome - 190000) * 0.45;
      }

      const medicareLevy = annualIncome * 0.02;
      return annualTax + medicareLevy;
    },
    calculateAustralianIncomeTaxMonthly: function(monthlyIncome) {
      const annualIncome = this.toNumber(monthlyIncome, 0) * 12;
      return this.calculateAustralianIncomeTaxAnnual(annualIncome) / 12;
    },
    isOwnerOccupiedProperty: function(prop) {
      if(!prop) {
        return false;
      }
      const ownerType = this.normalizeOwnershipTypeCode(prop.rawOwnerTypeCode || prop.rawOwnershipTypeCode || prop.type);
      return ownerType === 10;
    },
    formatLoanRepaymentInput: function(value) {
      const num = this.toNumber(value, 0);
      if(!(num > 0)) {
        return '0';
      }
      return Math.round(num).toLocaleString('en-AU');
    },
    getNegativeGearingRate: function(annualIncomeRaw) {
      const annualIncome = Math.max(this.toNumber(annualIncomeRaw, 0), 0);
      if(annualIncome <= 18200) {
        return 0;
      }
      if(annualIncome <= 45000) {
        return 0.21;
      }
      if(annualIncome <= 120000) {
        return 0.345;
      }
      if(annualIncome <= 180000) {
        return 0.39;
      }
      return 0.47;
    },
    fetchMyProperties: async function() {
      try {
        const ret = await storeSpecAction(storeId, 'memLstMyProperties', {});
        const normalized = ret.properties.map((item, index) => this.normalizeProperty(item, index));
        this.properties = this.sortPropertiesByPurchaseDateAsc(normalized);
        this.currentProperties = this.properties.filter((p) => !p.isSold);
        const existingLoans = this.profileForm.propertyLoans || {};
        const updatedLoans = {};
        this.currentProperties.forEach(function(p) {
          const mapped = this.toNumber(p.rawLoan, NaN);
          updatedLoans[p.id] = Number.isFinite(mapped) ? mapped : this.toNumber(existingLoans[p.id], 0);
        }, this);
        this.$set(this.profileForm, 'propertyLoans', updatedLoans);
        this.$set(this.profileForm, 'homeLoans', this.ownerOccupiedHomeLoans);
        this.$nextTick(() => {
          this.renderPropertyMarkers();
        });
        this.recalculateCashFlow();
        this.propertyActionError = '';
      } catch(error) {
        console.error('Failed to load member properties by storeSpecAction:', error);
      }
    },
    resetPropertyForm: function() {
      this.propertyForm = {
        propIdNo: null,
        serverTypeCode: null,
        location: '',
        ownerType: '1',
        holdingStatus: 'holding',
        purchasePrice: null,
        purchaseDate: '',
        valuation: null,
        valuationDate: '',
        soldPrice: null,
        soldDate: '',
        rentPw: null,
        loan: null,
        interestRate: null,
        loanRepaymentType: 1,
        loanMonthlyRepayment: null,
        rentalMgmtFeeRate: 12.5,
        rentalVacancyRate: 3,
        councilAndWaterRates: 3000,
        buildingAndLandlordsInsurance: 2000,
        maintenanceCost: 2000,
        landTax: 0,
        depreciationDiv43: 0,
        depreciationDiv40: 0
      };
    },
    openCreatePropertyDialog: function() {
      this.resetPropertyForm();
      this.propertyManagerError = '';
      this.showPropertyManagerDialog = true;
      this.activeTab = 'properties';
    },
    openEditPropertyDialog: async function(prop) {
      if(!prop) {
        return;
      }
      const fallbackManagedTypeCode = this.normalizeManagedTypeCode(prop.rawTypeCode);
      const fallbackOwnerTypeCode = this.normalizeOwnershipTypeCode(prop.rawOwnerTypeCode || prop.rawOwnershipTypeCode || prop.type);
      let latestProp = prop;
      try {
        const ret = await storeSpecAction(storeId, 'memLstMyProperties', { propIdNo: prop.id });
        const list = Array.isArray(ret && ret.properties) ? ret.properties : [];
        if(list.length > 0) {
          const latestSource = list[0] || {};
          const latestManagedTypeRaw = this.pick(latestSource, ['type', 'managedType']);
          const latestOwnerTypeRaw = this.pick(latestSource, ['ownerType', 'ownershipType', 'propertyType', 'category']);
          const latestManagedTypeCode = latestManagedTypeRaw === undefined
            ? fallbackManagedTypeCode
            : this.normalizeManagedTypeCode(latestManagedTypeRaw);
          const latestOwnerTypeCode = latestOwnerTypeRaw === undefined
            ? fallbackOwnerTypeCode
            : this.normalizeOwnershipTypeCode(latestOwnerTypeRaw);
          const normalizedLatest = this.normalizeProperty(latestSource, 0);
          latestProp = Object.assign({}, normalizedLatest, {
            rawTypeCode: latestManagedTypeCode,
            rawOwnerTypeCode: latestOwnerTypeCode,
            rawOwnershipTypeCode: latestOwnerTypeCode,
            type: latestOwnerTypeCode === 10 ? 'OWNER OCCUPIED' : 'INVESTMENT'
          });
        }
      } catch(error) {
        // Fallback to in-memory list item if latest fetch fails.
        console.error('Failed to fetch latest property details before edit:', error);
      }

      this.propertyForm = {
        propIdNo: latestProp.id,
        serverTypeCode: this.toNumber(latestProp.rawTypeCode, NaN),
        location: latestProp.address || '',
        type: latestProp.type,
        ownerType: String(this.normalizeOwnershipTypeCode(latestProp.rawOwnerTypeCode || latestProp.rawOwnershipTypeCode || latestProp.type)),
        holdingStatus: latestProp.isSold ? 'sold' : 'holding',
        purchasePrice: Number.isFinite(latestProp.rawPurchasePrice) ? latestProp.rawPurchasePrice : null,
        purchaseDate: this.toDateInput(latestProp.purchaseDate),
        valuation: Number.isFinite(latestProp.rawValuation) ? latestProp.rawValuation : null,
        valuationDate: this.toDateInput(latestProp.valuationDate),
        soldPrice: Number.isFinite(latestProp.rawSoldPrice) && latestProp.rawSoldPrice > 0 ? latestProp.rawSoldPrice : null,
        soldDate: this.toDateInput(latestProp.soldDate),
        rentPw: Number.isFinite(latestProp.rawRentPw) ? latestProp.rawRentPw : null,
        loan: Number.isFinite(latestProp.rawLoan) ? latestProp.rawLoan : null,
        interestRate: Number.isFinite(latestProp.rawInterestRate) ? latestProp.rawInterestRate : null,
        loanRepaymentType: this.toNumber(latestProp.loanRepaymentType, 1),
        loanMonthlyRepayment: Number.isFinite(latestProp.rawLoanMonthlyRepayment) ? latestProp.rawLoanMonthlyRepayment : null,
        rentalMgmtFeeRate: Number.isFinite(latestProp.rawRentalMgmtFeeRate) ? latestProp.rawRentalMgmtFeeRate : 12.5,
        rentalVacancyRate: Number.isFinite(latestProp.rawRentalVacancyRate) ? latestProp.rawRentalVacancyRate : 3,
        councilAndWaterRates: Number.isFinite(latestProp.rawCouncilAndWaterRates) ? latestProp.rawCouncilAndWaterRates : 3000,
        buildingAndLandlordsInsurance: Number.isFinite(latestProp.rawBuildingAndLandlordsInsurance) ? latestProp.rawBuildingAndLandlordsInsurance : 2000,
        maintenanceCost: Number.isFinite(latestProp.rawMaintenanceCost) ? latestProp.rawMaintenanceCost : 2000,
        landTax: Number.isFinite(latestProp.rawLandTax) ? latestProp.rawLandTax : 0,
        depreciationDiv43: Number.isFinite(latestProp.rawDepreciationDiv43) ? latestProp.rawDepreciationDiv43 : 0,
        depreciationDiv40: Number.isFinite(latestProp.rawDepreciationDiv40) ? latestProp.rawDepreciationDiv40 : 0
      };
      this.propertyManagerError = '';
      this.showPropertyManagerDialog = true;
    },
    closePropertyManagerDialog: function() {
      if(this.propertyManagerBusy) {
        return;
      }
      this.showPropertyManagerDialog = false;
      this.propertyManagerError = '';
    },
    canDeleteProperty: function(prop) {
      return this.normalizeManagedTypeCode(prop && prop.rawTypeCode) === 10;
    },
    openDeletePropertyConfirm: function(prop) {
      if(!prop || !prop.id || !this.canDeleteProperty(prop)) {
        return;
      }
      this.pendingDeleteProperty = prop;
      this.propertyActionError = '';
      this.showDeleteConfirmDialog = true;
    },
    closeDeletePropertyConfirm: function() {
      if(this.deletingPropertyId) {
        return;
      }
      this.showDeleteConfirmDialog = false;
      this.pendingDeleteProperty = null;
    },
    confirmDeleteProperty: async function() {
      if(!this.pendingDeleteProperty) {
        return;
      }
      await this.deleteMyProperty(this.pendingDeleteProperty);
    },
    submitPropertyForm: async function() {
      const ownerTypeCode = this.normalizeOwnershipTypeCode(this.propertyForm.ownerType);
      const isEdit = !!this.propertyForm.propIdNo;
      const serverTypeCode = this.toNumber(this.propertyForm.serverTypeCode, 10);
      const isInvestmentOnlyUpdate = isEdit && serverTypeCode === 1;
      const isSoldProperty = this.propertyForm.holdingStatus === 'sold';
      const shouldSubmitInvestmentFields = !isSoldProperty && (isInvestmentOnlyUpdate || ownerTypeCode === 1);
      const shouldSubmitLoanFields = !isSoldProperty && (isInvestmentOnlyUpdate || ownerTypeCode === 1 || ownerTypeCode === 10);

      const payload = {};

      if(!isInvestmentOnlyUpdate) {
        const address = String(this.propertyForm.location || '').trim();
        if(!address) {
          this.propertyManagerError = 'Address is required.';
          return;
        }
        payload.location = address;
        payload.ownerType = ownerTypeCode;
      }

      if(!isInvestmentOnlyUpdate) {
        const purchasePrice = this.toNumber(this.propertyForm.purchasePrice, NaN);
        if(Number.isFinite(purchasePrice) && purchasePrice >= 0) {
          payload.purchasePrice = Math.round(purchasePrice * 100);
        }
      }

      if(!isInvestmentOnlyUpdate && !isSoldProperty) {
        const valuation = this.toNumber(this.propertyForm.valuation, NaN);
        if(Number.isFinite(valuation) && valuation >= 0) {
          payload.valuation = Math.round(valuation * 100);
        }
      }

      if(isSoldProperty) {
        const soldPrice = this.toNumber(this.propertyForm.soldPrice, NaN);
        if(!(Number.isFinite(soldPrice) && soldPrice > 0)) {
          this.propertyManagerError = 'Sold price is required when status is Sold.';
          return;
        }
        if(!this.propertyForm.soldDate) {
          this.propertyManagerError = 'Sold date is required when status is Sold.';
          return;
        }
        payload.soldPrice = Math.round(soldPrice * 100);
        payload.soldDate = this.propertyForm.soldDate;
      } else {
        payload.soldPrice = 0;
        payload.soldDate = '';
      }

      if(shouldSubmitInvestmentFields) {
        const rentPw = this.toNumber(this.propertyForm.rentPw, NaN);
        if(Number.isFinite(rentPw) && rentPw >= 0) {
          payload.rentPw = Math.round(rentPw * 100);
        }
      } else {
        payload.rentPw = 0;
      }

      if(shouldSubmitLoanFields) {
        const loan = this.toNumber(this.propertyForm.loan, NaN);
        if(Number.isFinite(loan) && loan >= 0) {
          payload.loan = Math.round(loan * 100);
        }

        const interestRate = this.toNumber(this.propertyForm.interestRate, NaN);
        if(Number.isFinite(interestRate) && interestRate >= 0) {
          payload.interestRate = interestRate;
        }

        payload.loanRepaymentType = this.toNumber(this.propertyForm.loanRepaymentType, 1);

        const loanMonthlyRepayment = this.toNumber(this.calculatedLoanMonthlyRepayment, NaN);
        if(Number.isFinite(loanMonthlyRepayment) && loanMonthlyRepayment >= 0) {
          payload.loanMonthlyRepayment = Math.round(loanMonthlyRepayment * 100);
        }
      } else {
        payload.loan = 0;
        payload.interestRate = 0;
        payload.loanRepaymentType = 1;
        payload.loanMonthlyRepayment = 0;
      }

      if(shouldSubmitInvestmentFields) {
        const rentalMgmtFeeRate = this.toNumber(this.propertyForm.rentalMgmtFeeRate, NaN);
        if(Number.isFinite(rentalMgmtFeeRate) && rentalMgmtFeeRate >= 0) {
          payload.rentalMgmtFeeRate = rentalMgmtFeeRate;
        }

        const rentalVacancyRate = this.toNumber(this.propertyForm.rentalVacancyRate, NaN);
        if(Number.isFinite(rentalVacancyRate) && rentalVacancyRate >= 0) {
          payload.rentalVacancyRate = rentalVacancyRate;
        }

        const councilAndWaterRates = this.toNumber(this.propertyForm.councilAndWaterRates, NaN);
        if(Number.isFinite(councilAndWaterRates) && councilAndWaterRates >= 0) {
          payload.councilAndWaterRates = Math.round(councilAndWaterRates * 100);
        }

        const buildingAndLandlordsInsurance = this.toNumber(this.propertyForm.buildingAndLandlordsInsurance, NaN);
        if(Number.isFinite(buildingAndLandlordsInsurance) && buildingAndLandlordsInsurance >= 0) {
          payload.buildingAndLandlordsInsurance = Math.round(buildingAndLandlordsInsurance * 100);
        }

        const maintenanceCost = this.toNumber(this.propertyForm.maintenanceCost, NaN);
        if(Number.isFinite(maintenanceCost) && maintenanceCost >= 0) {
          payload.maintenanceCost = Math.round(maintenanceCost * 100);
        }

        const landTax = this.toNumber(this.propertyForm.landTax, NaN);
        if(Number.isFinite(landTax) && landTax >= 0) {
          payload.landTax = Math.round(landTax * 100);
        }

        const depreciationDiv43 = this.toNumber(this.propertyForm.depreciationDiv43, NaN);
        if(Number.isFinite(depreciationDiv43) && depreciationDiv43 >= 0) {
          payload.depreciationDiv43 = Math.round(depreciationDiv43 * 100);
        }

        const depreciationDiv40 = this.toNumber(this.propertyForm.depreciationDiv40, NaN);
        if(Number.isFinite(depreciationDiv40) && depreciationDiv40 >= 0) {
          payload.depreciationDiv40 = Math.round(depreciationDiv40 * 100);
        }
      } else {
        payload.rentalMgmtFeeRate = 0;
        payload.rentalVacancyRate = 0;
        payload.councilAndWaterRates = 0;
        payload.buildingAndLandlordsInsurance = 0;
        payload.maintenanceCost = 0;
        payload.landTax = 0;
        payload.depreciationDiv43 = 0;
        payload.depreciationDiv40 = 0;
      }

      if(!isInvestmentOnlyUpdate) {
        if(this.propertyForm.purchaseDate) {
          payload.purchaseDate = this.propertyForm.purchaseDate;
        }
        if(!isSoldProperty && this.propertyForm.valuationDate) {
          payload.valuationDate = this.propertyForm.valuationDate;
        }
      }

      if(isEdit) {
        payload.propIdNo = this.propertyForm.propIdNo;
      }

      this.propertyManagerBusy = true;
      this.propertyManagerError = '';
      this.propertyActionError = '';

      try {
        await storeSpecAction(storeId, 'memAddOrUpdateOwnProperties', { rcds: [payload] });
        this.showPropertyManagerDialog = false;
        await this.fetchMyProperties();
      } catch(error) {
        const msg = error && error.message ? error.message : '';
        this.propertyManagerError = msg || 'Failed to save property.';
      } finally {
        this.propertyManagerBusy = false;
      }
    },
    deleteMyProperty: async function(prop) {
      if(!prop || !prop.id) {
        return;
      }
      if(!this.canDeleteProperty(prop)) {
        this.propertyActionError = 'Only member self managed properties can be deleted.';
        return;
      }
      this.deletingPropertyId = prop.id;
      this.propertyActionError = '';
      try {
        await storeSpecAction(storeId, 'memDeleteOwnProperty', { propIdNo: prop.id });
        this.showDeleteConfirmDialog = false;
        this.pendingDeleteProperty = null;
        await this.fetchMyProperties();
      } catch(error) {
        const msg = error && error.message ? error.message : '';
        this.propertyActionError = msg || 'Failed to delete property.';
      } finally {
        this.deletingPropertyId = null;
      }
    },
    setPropertyLoan: function(propId, val) {
      this.$set(this.profileForm.propertyLoans, propId, this.toNumber(parseFloat(val), 0));
    },
    computeValuationChart: function(prop) {
      if(!prop) { return null; }
      const startValue = this.toNumber(prop.rawPurchasePrice, NaN);
      const endValue = prop.isSold
        ? this.toNumber(prop.rawSoldPrice, NaN)
        : this.toNumber(prop.rawValuation, NaN);
      if(!Number.isFinite(startValue) || startValue <= 0 || !Number.isFinite(endValue) || endValue <= 0) {
        return null;
      }
      const startDate = prop.purchaseDate;
      const endDate = prop.isSold ? prop.soldDate : prop.valuationDate;

      const W = 460;
      const H = 250;
      const padT = 20;
      const padB = 52;
      const padL = 62;
      const padR = 14;
      const innerW = W - padL - padR;
      const innerH = H - padT - padB;

      // Build series of {date, value} points from purchase + valuationHistory + (sold or current valuation).
      const parseDate = (d) => {
        if(!d) { return NaN; }
        const t = new Date(d).getTime();
        return Number.isFinite(t) ? t : NaN;
      };
      const points = [];
      const startTs = parseDate(startDate);
      points.push({ ts: Number.isFinite(startTs) ? startTs : 0, value: startValue, date: startDate });

      const history = Array.isArray(prop.valuationHistory) ? prop.valuationHistory : [];
      history.forEach((h) => {
        if(!h) { return; }
        const v = this.toNumber(this.pick(h, ['valuation']), NaN) / 100;
        const d = this.pick(h, ['valuationDate']);
        const ts = parseDate(d);
        if(Number.isFinite(v) && v > 0 && Number.isFinite(ts)) {
          points.push({ ts: ts, value: v, date: d });
        }
      });

      const endTs = parseDate(endDate);
      points.push({ ts: Number.isFinite(endTs) ? endTs : (points[points.length - 1].ts + 1), value: endValue, date: endDate });

      // Sort by timestamp ascending and dedupe consecutive identical timestamps (prefer later value).
      points.sort((a, b) => a.ts - b.ts);
      const dedup = [];
      points.forEach((p) => {
        if(dedup.length && dedup[dedup.length - 1].ts === p.ts) {
          dedup[dedup.length - 1] = p;
        } else {
          dedup.push(p);
        }
      });

      // If we only have the two synthetic endpoints, interpolate an eased curve for visual smoothness.
      let series;
      if(dedup.length <= 2) {
        const N = 14;
        series = [];
        for(let i = 0; i < N; i += 1) {
          const t = i / (N - 1);
          const eased = Math.pow(t, 1.35);
          series.push({ value: startValue + (endValue - startValue) * eased, frac: t });
        }
      } else {
        const minTs = dedup[0].ts;
        const maxTs = dedup[dedup.length - 1].ts;
        const span = Math.max(maxTs - minTs, 1);
        series = dedup.map((p) => ({ value: p.value, frac: (p.ts - minTs) / span }));
      }

      const values = series.map((s) => s.value);
      const minY = Math.min.apply(null, values);
      const maxY = Math.max.apply(null, values);
      const range = Math.max(maxY - minY, 1);
      const coords = series.map((s) => {
        const x = padL + s.frac * innerW;
        const yy = padT + (1 - (s.value - minY) / range) * innerH;
        return { x: Number(x.toFixed(2)), y: Number(yy.toFixed(2)) };
      });

      const linePath = 'M ' + coords.map((c) => c.x + ' ' + c.y).join(' L ');
      const areaPath = linePath
        + ' L ' + coords[coords.length - 1].x + ' ' + (padT + innerH)
        + ' L ' + coords[0].x + ' ' + (padT + innerH) + ' Z';

      const delta = endValue - startValue;
      const deltaPct = startValue > 0 ? (delta / startValue) * 100 : 0;

      const formatAxisDate = (d) => {
        const mm = moment(d);
        return mm.isValid() ? mm.format('YYYY-MM-DD') : '—';
      };
      const yTicksRaw = [maxY, (maxY + minY) / 2, minY];
      const yTicks = yTicksRaw.map((v) => ({
        label: this.formatCompactMoney(v),
        y: Number((padT + (1 - (v - minY) / range) * innerH).toFixed(2))
      }));

      let midTickLabel = '—';
      if(Number.isFinite(startTs) && Number.isFinite(endTs)) {
        midTickLabel = formatAxisDate((startTs + endTs) / 2);
      }
      const xTicks = [
        { label: formatAxisDate(startDate), x: padL + 2, anchor: 'start' },
        { label: midTickLabel, x: Number((padL + innerW * 0.5).toFixed(2)), anchor: 'middle' },
        { label: formatAxisDate(endDate), x: padL + innerW - 2, anchor: 'end' }
      ];

      return {
        viewBox: '0 0 ' + W + ' ' + H,
        linePath: linePath,
        areaPath: areaPath,
        startPoint: coords[0],
        endPoint: coords[coords.length - 1],
        midPoints: coords.slice(1, -1),
        axisLeft: padL,
        axisRight: padL + innerW,
        baseY: padT + innerH,
        yTicks: yTicks,
        xTicks: xTicks,
        startLabel: this.formatCompactMoney(startValue),
        endLabel: this.formatCompactMoney(endValue),
        startDate: startDate ? this.formatDate(startDate) : '—',
        endDate: endDate ? this.formatDate(endDate) : '—',
        endCaption: prop.isSold ? 'Sold' : 'Valuation',
        delta: delta,
        deltaPct: deltaPct,
        deltaText: (delta >= 0 ? '+' : '') + this.formatCompactMoney(Math.abs(delta)) + ' (' + (deltaPct >= 0 ? '+' : '') + deltaPct.toFixed(1) + '%)',
        isPositive: delta >= 0
      };
    },
    inferAustralianState: function(address) {
      const a = String(address || '').toUpperCase();
      if(/\bNSW\b|NEW SOUTH WALES|SYDNEY/.test(a)) { return 'NSW'; }
      if(/\bVIC\b|VICTORIA|MELBOURNE/.test(a)) { return 'VIC'; }
      if(/\bQLD\b|QUEENSLAND|BRISBANE|GOLD COAST/.test(a)) { return 'QLD'; }
      if(/\bWA\b|WESTERN AUSTRALIA|PERTH/.test(a)) { return 'WA'; }
      if(/\bSA\b|SOUTH AUSTRALIA|ADELAIDE/.test(a)) { return 'SA'; }
      if(/\bTAS\b|TASMANIA|HOBART/.test(a)) { return 'TAS'; }
      if(/\bACT\b|CANBERRA/.test(a)) { return 'ACT'; }
      if(/\bNT\b|NORTHERN TERRITORY|DARWIN/.test(a)) { return 'NT'; }
      return 'AU';
    },
    australiaStateCoordinates: function(state) {
      const points = {
        WA: { lat: -31.95, lng: 115.86 },
        NT: { lat: -12.46, lng: 130.84 },
        SA: { lat: -34.93, lng: 138.6 },
        QLD: { lat: -27.47, lng: 153.03 },
        NSW: { lat: -33.87, lng: 151.21 },
        ACT: { lat: -35.28, lng: 149.13 },
        VIC: { lat: -37.81, lng: 144.96 },
        TAS: { lat: -42.88, lng: 147.32 },
        AU: { lat: -25.2744, lng: 133.7751 }
      };
      return points[state] || points.AU;
    },
    formatDate: function(date) {
      return moment(date).format('YYYY-MM-DD');
    },
    toDateInput: function(date) {
      const mm = moment(date);
      return mm.isValid() ? mm.format('YYYY-MM-DD') : '';
    },
    normalizeManagedTypeCode: function(value) {
      return this.toNumber(value, 10) === 1 ? 1 : 10;
    },
    normalizeOwnershipTypeCode: function(value) {
      if(value === 10 || value === '10') {
        return 10;
      }
      const raw = String(value || '').toUpperCase();
      if(raw === 'OWNER OCCUPIED' || raw === 'OWNER_OCCUPIED' || raw === 'OO') {
        return 10;
      }
      return 1;
    },
    sortPropertiesByPurchaseDateAsc: function(properties) {
      const list = Array.isArray(properties) ? properties.slice() : [];
      const fallbackTime = Number.MAX_SAFE_INTEGER;
      const toTs = (prop) => {
        const m = moment(prop && prop.purchaseDate);
        return m.isValid() ? m.valueOf() : fallbackTime;
      };

      list.sort((a, b) => {
        const diff = toTs(a) - toTs(b);
        if(diff !== 0) {
          return diff;
        }
        return String((a && a.address) || '').localeCompare(String((b && b.address) || ''));
      });

      return list;
    },
    normalizeProperty: function(item, index) {
      const source = item && typeof item === 'object' ? item : {};
      const managedTypeRaw = this.pick(source, ['type', 'managedType']);
      const ownerTypeRaw = this.pick(source, ['ownerType', 'ownershipType', 'propertyType', 'category']);
      const managedTypeCode = this.normalizeManagedTypeCode(managedTypeRaw);
      const ownerTypeCode = this.normalizeOwnershipTypeCode(ownerTypeRaw);
      const type = ownerTypeCode === 10 ? 'OWNER OCCUPIED' : 'INVESTMENT';
      const purchased = this.toNumber(this.pick(source, ['purchasePrice']), 0) / 100;
      const value = this.toNumber(this.pick(source, ['valuation']), 0) / 100;
      const equity = this.toNumber(this.pick(source, ['equity', 'totalEquity']), 0);
      const loan = this.toNumber(this.pick(source, ['loan', 'loanAmount', 'outstandingLoan']), 0);
      const yieldRate = this.toNumber(this.pick(source, ['yield', 'rentalYield', 'yieldRate']), NaN);
      const rate = this.toNumber(this.pick(source, ['rate', 'interestRate']), NaN);
      const lvrFromApi = this.toNumber(this.pick(source, ['lvr', 'loanToValueRatio']), NaN);
      const lvr = Number.isFinite(lvrFromApi)
        ? lvrFromApi
        : (value > 0 ? (loan / value) * 100 : NaN);
      const rentPw = this.toNumber(this.pick(source, ['rentPw', 'weeklyRent', 'rentPerWeek']), NaN);
      const rawLoan = this.toNumber(this.pick(source, ['loan', 'loanAmount', 'outstandingLoan']), NaN);
      const rawLoanMonthlyRepayment = this.toNumber(this.pick(source, ['loanMonthlyRepayment', 'monthlyLoanRepayment']), NaN);
      const rawInterestRate = this.toNumber(this.pick(source, ['interestRate', 'rate']), NaN);
      const rawRentalMgmtFeeRate = this.toNumber(this.pick(source, ['rentalMgmtFeeRate', 'rentalManagementFeeRate']), NaN);
      const rawRentalVacancyRate = this.toNumber(this.pick(source, ['rentalVacancyRate']), NaN);
      const rawCouncilAndWaterRates = this.toNumber(this.pick(source, ['councilAndWaterRates']), NaN);
      const rawBuildingAndLandlordsInsurance = this.toNumber(this.pick(source, ['buildingAndLandlordsInsurance']), NaN);
      const rawMaintenanceCost = this.toNumber(this.pick(source, ['maintenanceCost']), NaN);
      const rawLandTax = this.toNumber(this.pick(source, ['landTax']), NaN);
      const rawDepreciationDiv43 = this.toNumber(this.pick(source, ['depreciationDiv43', 'depreciation43', 'capitalWorksDivision43']), NaN);
      const rawDepreciationDiv40 = this.toNumber(this.pick(source, ['depreciationDiv40', 'depreciation40', 'plantEquipmentDivision40']), NaN);

      const pitem = {
        id: this.pick(source, ['propIdNo']) || index + 1,
        type: type,
        rawTypeCode: managedTypeCode,
        rawOwnerTypeCode: ownerTypeCode,
        rawOwnershipTypeCode: ownerTypeCode,
        structure: this.pick(source, ['structure']) || '—',
        isSold: !!(item.soldDate || item.soldPrice),
        rawValuation: value,
        rawCapitalGain: this.toNumber(this.pick(source, ['capitalGain']), 0) / 100,
        rawRentPw: Number.isFinite(rentPw) ? rentPw / 100 : 0,
        rawPurchasePrice: purchased,
        purchaseDate: this.pick(source, ['purchaseDate']) || '',
        valuationDate: this.pick(source, ['valuationDate']) || '',
        rawSoldPrice: this.toNumber(this.pick(source, ['soldPrice']), 0) / 100,
        soldDate: this.pick(source, ['soldDate']) || '',
        rawLoan: Number.isFinite(rawLoan) ? rawLoan / 100 : NaN,
        rawInterestRate: rawInterestRate,
        loanRepaymentType: this.toNumber(this.pick(source, ['loanRepaymentType']), 1),
        rawLoanMonthlyRepayment: Number.isFinite(rawLoanMonthlyRepayment) ? rawLoanMonthlyRepayment / 100 : NaN,
        rawRentalMgmtFeeRate: rawRentalMgmtFeeRate,
        rawRentalVacancyRate: rawRentalVacancyRate,
        rawCouncilAndWaterRates: Number.isFinite(rawCouncilAndWaterRates) ? rawCouncilAndWaterRates / 100 : NaN,
        rawBuildingAndLandlordsInsurance: Number.isFinite(rawBuildingAndLandlordsInsurance) ? rawBuildingAndLandlordsInsurance / 100 : NaN,
        rawMaintenanceCost: Number.isFinite(rawMaintenanceCost) ? rawMaintenanceCost / 100 : NaN,
        rawLandTax: Number.isFinite(rawLandTax) ? rawLandTax / 100 : NaN,
        rawDepreciationDiv43: Number.isFinite(rawDepreciationDiv43) ? rawDepreciationDiv43 / 100 : 0,
        rawDepreciationDiv40: Number.isFinite(rawDepreciationDiv40) ? rawDepreciationDiv40 / 100 : 0,
        image: this.pick(source, ['image', 'imageUrl', 'photo', 'thumbnail']) || '',
        valuationHistory: Array.isArray(this.pick(source, ['valuationHistory'])) ? this.pick(source, ['valuationHistory']) : [],
        address: this.pick(source, ['location', 'address']) || 'Property',
        metrics: [
          { label: 'PURCHASED', value: this.formatCompactMoney(purchased) },
          { label: 'PURCHASE DATE', value: this.formatDate(this.pick(source, ['purchaseDate'])) },
          // { label: 'EQUITY', value: this.formatCompactMoney(equity) },
          // { label: 'YIELD', value: this.formatPercent(yieldRate) },
          // { label: 'LOAN', value: this.formatCompactMoney(loan) },
          // { label: 'RENT /WK', value: this.formatRentPerWeek(this.toNumber(this.pick(source, ['rentPw', 'weeklyRent', 'rentPerWeek']), NaN)) },
          // { label: 'RATE', value: this.formatPercent(rate) },
          // { label: 'LVR', value: this.formatPercent(lvr) }
        ]
      };
      if(!pitem.isSold) {
        pitem.metrics.push({ label: 'CURRENT VALUE', value: this.formatCompactMoney(value) });
        pitem.metrics.push({ label: 'VALUATION DATE', value: this.formatDate(this.pick(source, ['valuationDate'])) });
      } else {
        pitem.metrics.push({ label: 'SOLD PRICE', value: this.formatCompactMoney(this.toNumber(this.pick(source, ['soldPrice']), 0) / 100) });
        pitem.metrics.push({ label: 'SOLD DATE', value: this.formatDate(this.pick(source, ['soldDate'])) });
      }
      pitem.metrics.push({ label: 'CAPTIAL GAIN', value: this.formatCompactMoney(this.toNumber(this.pick(source, ['capitalGain']), 0) / 100) });
      pitem.metrics.push({ label: 'ANNUAL GROWTH', value: this.formatPercent(this.toNumber(this.pick(source, ['annualGrowth']), NaN)) });
      pitem.metrics.push({ label: 'RENTAL PW', value: this.formatCompactMoney(Number.isFinite(rentPw) ? rentPw / 100 : 0) });
      pitem.metrics.push({ label: 'RENTAL YIELD', value: this.formatPercent(this.toNumber(this.pick(source, ['rentalYield']), NaN)) });
      return pitem;
    },
    pick: function(source, keys) {
      if(!source || typeof source !== 'object' || !Array.isArray(keys)) {
        return undefined;
      }

      for(let i = 0; i < keys.length; i += 1) {
        const value = source[keys[i]];
        if(value !== undefined && value !== null && value !== '') {
          return value;
        }
      }

      return undefined;
    },
    toNumber: function(value, fallback) {
      if(typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }

      if(typeof value === 'string') {
        const parsed = Number(value.replace(/[^\d.-]/g, ''));
        return Number.isFinite(parsed) ? parsed : fallback;
      }

      return fallback;
    },
    formatCompactMoney: function(value) {
      if(!Number.isFinite(value) || value <= 0) {
        return '—';
      }

      if(value >= 1000000) {
        return '$' + (value / 1000000).toFixed(3).replace(/0+$/, '').replace(/\.$/, '') + 'm';
      }

      return '$' + Math.round(value).toLocaleString('en-AU');
    },
    formatPercent: function(value) {
      if(!Number.isFinite(value)) {
        return '—';
      }
      return value.toFixed(2) + '%';
    },
    formatRentPerWeek: function(value) {
      if(!Number.isFinite(value) || value <= 0) {
        return '—';
      }
      return '$' + Math.round(value).toLocaleString('en-AU');
    },
    formatCurrency: function(value) {
      const num = this.toNumber(value, 0);
      const abs = Math.abs(Math.round(num));
      const sign = num < 0 ? '-$' : '$';
      return sign + abs.toLocaleString('en-AU');
    },
    doLogout: function() {
      this.destroyBasemap();
      this.authenticated = false;
      sessionStorage.removeItem('portal_auth');
      sessionStorage.removeItem('portal_user');
      sessionStorage.removeItem('portal_token');
      sessionStorage.removeItem('portal_profile');
      this.loginForm = { email: '', password: '' };
      this.loginError = '';
      this.profileError = '';
      this.passwordError = '';
      this.passwordSuccess = '';
      this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' };
      this.activeTab = 'dashboard';
    }
  }
};
</script>

<style scoped>
/* ── ROOT ── */
.portal-root {
  min-height: 100vh;
}

/* ── LOGIN ── */
.login-screen {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 16px;
  background:
    radial-gradient(circle at 15% 20%, rgba(47, 122, 108, 0.28), transparent 40%),
    radial-gradient(circle at 85% 80%, rgba(203, 135, 69, 0.18), transparent 36%),
    linear-gradient(150deg, #0a2c22 0%, #0e3d30 55%, #0b3328 100%);
}

.login-card {
  width: min(440px, 100%);
  background: #ffffff;
  border-radius: 20px;
  padding: 42px 38px;
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.36);
  text-align: center;
}

.login-logo {
  height: 52px;
  width: auto;
  margin-bottom: 22px;
}

.login-card h2 {
  margin: 0 0 8px;
  color: #1e4c40;
  font-size: 1.85rem;
  font-weight: 700;
}

.login-sub {
  margin: 0 0 28px;
  color: #5e7972;
  font-size: 0.95rem;
}

.login-form {
  text-align: left;
}

.field {
  margin-bottom: 16px;
}

.field label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.88rem;
  color: #3a5c54;
  font-weight: 600;
}

.field input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #b8d4ce;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #2f4c45;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.field input:focus {
  outline: none;
  border-color: #2f7a6c;
  box-shadow: 0 0 0 3px rgba(47, 122, 108, 0.12);
}

.login-error {
  margin: 0 0 12px;
  color: #c0392b;
  font-size: 0.86rem;
  text-align: left;
}

.login-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #2f7a6c 0%, #1e5f53 100%);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  transition: filter 0.2s;
}

.login-btn:hover {
  filter: brightness(1.1);
}

.demo-hint {
  margin: 22px 0 0;
  font-size: 0.82rem;
  color: #7b918b;
  line-height: 1.6;
}

.login-guide-link {
  display: inline-block;
  margin-top: 10px;
  font-size: 0.84rem;
  color: #2f7a6c;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.login-guide-link:hover {
  color: #1e5f53;
}

.demo-hint code {
  background: #f0f5f3;
  padding: 2px 6px;
  border-radius: 5px;
  color: #2f4c45;
  font-size: 0.82rem;
}

/* ── DASHBOARD LAYOUT ── */
.dashboard {
  display: flex;
  align-items: flex-start;
  min-height: 100vh;
}

/* SIDEBAR */
.portal-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #0e3a2f;
  color: #c8e3dc;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
  transition: width 0.22s ease;
  z-index: 10;
}

.portal-sidebar.is-collapsed {
  width: 64px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 14px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(160, 210, 198, 0.14);
  min-height: 62px;
}

.brand-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3c9d87, #2d7463);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 900;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.brand-text {
  font-size: 1.05rem;
  font-weight: 700;
  color: #edf8f5;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  flex: 1;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  color: rgba(205, 234, 224, 0.82);
  font-size: 0.9rem;
  font-weight: 500;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  user-select: none;
}

.sidebar-link:hover {
  background: rgba(63, 155, 132, 0.2);
  color: #edf8f5;
}

.sidebar-link.is-active {
  background: rgba(47, 122, 108, 0.38);
  color: #ffffff;
  font-weight: 700;
}

.sidebar-icon {
  font-size: 1.15rem;
  flex-shrink: 0;
}

.sidebar-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-bottom {
  padding: 10px 10px 20px;
  border-top: 1px solid rgba(160, 210, 198, 0.14);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.collapse-hint {
  padding: 6px 12px;
  font-size: 0.78rem;
  color: rgba(205, 234, 224, 0.44);
  cursor: pointer;
  text-align: right;
  user-select: none;
}

.collapse-hint:hover {
  color: rgba(205, 234, 224, 0.7);
}

/* MAIN */
.portal-main {
  flex: 1;
  min-width: 0;
  background: #eef5f3;
  min-height: 100vh;
}

.welcome-banner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 32px;
  background:
    radial-gradient(circle at 88% 14%, rgba(255, 255, 255, 0.18), transparent 38%),
    linear-gradient(135deg, #0f4d40 0%, #2f7a6c 55%, #3f9a85 100%);
  border: 1px solid #1f5a4d;
  border-radius: 18px;
  color: #ffffff;
  box-shadow: 0 18px 38px rgba(15, 77, 64, 0.22);
  overflow: hidden;
}

.welcome-banner::before {
  content: '';
  position: absolute;
  inset: -40% -10% auto auto;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.14), transparent 65%);
  pointer-events: none;
}

.welcome-text {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.welcome-hi {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.82);
}

.welcome-lead {
  margin: 4px 0 0;
  font-size: 1.05rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
}

.welcome-amount {
  margin: 6px 0 4px;
  font-size: clamp(2.6rem, 5vw, 4rem);
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.01em;
  color: #ffffff;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
}

.welcome-sub {
  margin: 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.78);
  letter-spacing: 0.01em;
}

.welcome-badge {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
  color: #ffffff;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  white-space: nowrap;
}

.welcome-badge-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  font-size: 0.85rem;
  letter-spacing: 0;
}

@media (max-width: 768px) {
  .welcome-banner {
    flex-direction: column;
    align-items: flex-start;
    padding: 22px 20px;
  }
}

.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px 18px;
  background: #ffffff;
  border-bottom: 1px solid #d2e3de;
}

.dash-title {
  margin: 0;
  font-size: 1.6rem;
  color: #1e4c40;
  font-weight: 700;
}

.dash-updated {
  margin: 4px 0 0;
  font-size: 0.84rem;
  color: #7b918b;
}

.add-btn {
  padding: 9px 20px;
  border: none;
  border-radius: 10px;
  background: #2f7a6c;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;
}

.add-btn:hover {
  background: #236a5d;
}

.content {
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 34px;
}


.sec-heading {
  margin: 0 0 16px;
  font-size: 1.15rem;
  color: #1e4c40;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.sec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.sec-row .sec-heading {
  margin-bottom: 0;
}

.sec-link {
  font-size: 0.88rem;
  color: #2f7a6c;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
}

.sec-link:hover {
  text-decoration: underline;
}

@media (min-width: 769px) {
  .content>.dash-section {
    width: 1110px;
    max-width: 1110px;
    margin: 0 auto;
  }

  .balance-map-row {
    width: 1110px;
    max-width: 1110px;
    margin: 0 auto;
  }
}

/* BALANCE SHEET */
.balance-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.balance-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  padding: 18px 20px;
}

.b-label {
  margin: 0 0 8px;
  font-size: 0.73rem;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: #7b918b;
  font-weight: 700;
}

.b-value {
  margin: 0;
  font-size: 1.55rem;
  font-weight: 700;
  color: #1e4c40;
}

.balance-card.card-highlight {
  background: #f0faf6;
  border-color: #a8d5c8;
}

.balance-card.card-loans .b-value {
  color: #b04a30;
}

.balance-card.card-networth {
  background: linear-gradient(145deg, #1a5f51, #0e3d30);
  border-color: transparent;
}

.balance-card.card-networth .b-label {
  color: rgba(205, 234, 224, 0.7);
}

.balance-card.card-networth .b-value {
  color: #d4efe7;
}

/* PROPERTIES */
.prop-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prop-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.prop-card:hover {
  border-color: #2f7a6c;
  box-shadow: 0 4px 18px rgba(47, 122, 108, 0.13);
}

.prop-card.prop-card-static {
  cursor: default;
}

.prop-card.prop-card-static:hover {
  border-color: #d2e3de;
  box-shadow: none;
}

.prop-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #eef5f3;
}

.prop-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  text-transform: uppercase;
}

.badge-inv {
  background: #e6f5f1;
  color: #1a5f48;
}

.badge-oo {
  background: #fff3e0;
  color: #885200;
}

.badge-norland {
  background: #e6f5f1;
  color: #1a5f48;
}

.badge-own {
  background: #fff3e0;
  color: #885200;
}

.sold-badge {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #ffffff;
  background: #c0392b;
  flex-shrink: 0;
}

.prop-address {
  font-size: 0.92rem;
  font-weight: 600;
  color: #2f4c45;
}

.prop-update-btn {
  margin-left: auto;
  border: 1px solid #2f7a6c;
  background: #ffffff;
  color: #2f7a6c;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.prop-update-btn:hover {
  background: #edf8f5;
}

.prop-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
}

.prop-actions .prop-update-btn {
  margin-left: 0;
}

.prop-delete-btn {
  border-color: #c0392b;
  color: #c0392b;
}

.prop-delete-btn:hover {
  background: #fdecea;
}

.prop-metrics {
  display: flex;
  flex-wrap: wrap;
  padding: 12px 16px 14px;
  gap: 18px 24px;
}

.prop-card-detailed .prop-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 18px;
  padding: 16px;
}

.prop-card-detailed .prop-left {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.prop-image {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background:
    radial-gradient(circle at 22% 24%, rgba(255, 255, 255, 0.45), transparent 42%),
    linear-gradient(140deg, #4a8f7e 0%, #2f7a6c 55%, #1e5f53 100%);
  color: rgba(255, 255, 255, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  overflow: hidden;
  box-shadow: inset 0 -40px 60px rgba(0, 0, 0, 0.18);
}

.prop-image i {
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.18));
}

.prop-image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 58%;
  display: block;
}

.prop-card-detailed .prop-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 0;
  gap: 10px;
}

.prop-card-detailed .prop-right {
  display: flex;
  min-width: 0;
  align-items: flex-start;
}

.prop-chart-card {
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #f3faf7 0%, #ffffff 100%);
  border: 1px solid #d2e3de;
  border-radius: 12px;
  padding: 16px 16px 14px;
  min-width: 0;
  width: 100%;
}

.prop-chart-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.prop-chart-title {
  margin: 0;
  font-size: 0.82rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 700;
  color: #1e4c40;
}

.prop-chart-sub {
  margin: 4px 0 0;
  font-size: 0.8rem;
  color: #7b918b;
}

.prop-chart-delta {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.prop-chart-delta.pos {
  background: #e6f5ee;
  color: #1e7a5f;
}

.prop-chart-delta.neg {
  background: #fdecea;
  color: #b3261e;
}

.prop-chart-svg {
  width: 100%;
  height: auto;
  aspect-ratio: 460 / 250;
  min-height: 168px;
  max-height: 240px;
  display: block;
  margin: 2px 0 10px;
}

.prop-chart-foot {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-top: auto;
  padding-top: 6px;
  border-top: 1px dashed #d2e3de;
}

.pcf-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pcf-col-end {
  text-align: right;
  align-items: flex-end;
}

.pcf-label {
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7b918b;
  font-weight: 700;
}

.pcf-value {
  font-size: 1rem;
  font-weight: 700;
  color: #1e4c40;
}

.prop-chart-empty {
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d2e3de;
  border-radius: 12px;
  color: #7b918b;
  font-size: 0.88rem;
  padding: 24px;
  width: 100%;
}

@media (max-width: 900px) {
  .prop-card-detailed .prop-body {
    grid-template-columns: 1fr;
  }

  .prop-card-detailed .prop-right {
    align-items: stretch;
  }

  .prop-card-detailed .prop-metrics {
    grid-template-columns: 1fr;
  }

  .prop-chart-svg {
    min-height: 156px;
  }
}

.pm {
  display: grid;
  gap: 6px;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #dbe9e4;
  border-radius: 10px;
  background: #f7fbfa;
}

.pm-label {
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #7b918b;
  font-weight: 700;
}

.pm-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e4c40;
  line-height: 1.25;
  word-break: break-word;
}

/* CASH FLOW */
.cashflow-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  overflow: hidden;
  max-width: 500px;
}

.cashflow-card--wide {
  max-width: none;
}

.cashflow-note {
  margin: 0 auto 10px;
  color: #5e7972;
  font-size: 0.86rem;
  line-height: 1.5;
}

.cashflow-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 14px;
}

.cashflow-summary-column {
  border: 1px solid #dbe9e4;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.cashflow-summary-title {
  padding: 12px 14px;
  background: linear-gradient(180deg, #f4f9f7, #edf5f3);
  border-bottom: 1px solid #dbe9e4;
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e4c40;
}

.cashflow-summary-list {
  display: flex;
  flex-direction: column;
}

.cf-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 18px;
  font-size: 0.92rem;
  color: #2f4c45;
  border-bottom: 1px solid #eef5f3;
}

.cf-row:last-child {
  border-bottom: none;
}

.cf-row.cf-subtotal {
  background: #f0faf6;
  font-weight: 700;
}

.cf-row.cf-final {
  background: linear-gradient(90deg, #1a5f51, #0e3d30);
  color: #edf8f5;
  font-weight: 700;
  font-size: 1rem;
}

.cf-neg {
  color: #c0392b;
}

.cf-pos {
  color: #1a8060;
}

.cf-row.cf-final .cf-neg,
.cf-row.cf-final .cf-pos {
  color: #d4efe7;
}

.cashflow-summary-column .cf-row:last-child {
  border-bottom: none;
}

.cf-divider {
  height: 1px;
  background: #d7e6e1;
}

.cf-property-cards {
  max-width: 1160px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
}

.cf-property-card {
  max-width: none;
}

.cf-prop-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 11px 18px;
  background: #f7fbfa;
  border-bottom: 1px solid #eef5f3;
}

.cf-prop-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1e4c40;
}

.cf-prop-update-btn {
  padding: 5px 10px;
  font-size: 0.78rem;
}

/* FINANCIAL PROFILE FORM */
.fp-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
}

.fp-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: #f4f9f7;
  border-bottom: 1px solid #d2e3de;
  font-size: 0.97rem;
  font-weight: 700;
  color: #1e4c40;
}

.fp-card-header .fp-card-save-btn {
  margin-left: auto;
}

.fp-icon {
  font-size: 1.1rem;
  color: #2f7a6c;
  flex-shrink: 0;
}

.fp-note {
  font-style: normal;
  font-weight: 400;
  font-size: 0.8rem;
  color: #7b918b;
  margin-left: 6px;
}

.fp-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 16px 20px;
}

.fp-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.fp-field>span {
  font-size: 0.79rem;
  font-weight: 700;
  color: #4e6c65;
  letter-spacing: 0.01em;
}

.fp-prop-label {
  font-size: 0.79rem;
  font-weight: 700;
  color: #4e6c65;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fp-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #c2d8d2;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.fp-pre {
  padding: 0 8px;
  font-size: 0.88rem;
  color: #7b918b;
  background: #f4f9f7;
  border-right: 1px solid #c2d8d2;
  line-height: 36px;
  flex-shrink: 0;
}

.fp-wrap input {
  flex: 1;
  border: none;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: #274137;
  min-width: 0;
  background: transparent;
}

.fp-wrap input:focus {
  outline: none;
}

.fp-field--ro .fp-wrap {
  background: #f4f9f7;
}

.fp-field--ro .fp-wrap input {
  color: #7b918b;
  cursor: default;
}

.fp-sub-section {
  padding: 0 20px 16px;
  border-top: 1px dashed #d2e3de;
  margin-top: 4px;
}

.fp-sub-title {
  margin: 14px 0 10px;
  font-size: 0.82rem;
  font-weight: 700;
  color: #5e7972;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.fp-field-loan {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.fp-loan-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fp-loan-row .fp-wrap {
  flex: 1;
}

.fp-wrap-ro {
  background: #f4f9f7;
}

.fp-wrap-ro input {
  color: #7b918b;
  cursor: default;
}

.fp-loan-update-btn {
  flex-shrink: 0;
  border: 1px solid #2f7a6c;
  background: #ffffff;
  color: #2f7a6c;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.fp-loan-update-btn:hover {
  background: #edf8f5;
}

.fp-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid #d2e3de;
  background: #f4f9f7;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e4c40;
}

.fp-total-row--red {
  color: #b04a30;
}

.fp-total-val {
  font-size: 1.05rem;
}

.income-form-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  padding: 18px;
}

.income-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.income-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.income-field span {
  font-size: 0.82rem;
  color: #4e6c65;
  font-weight: 700;
}

.income-field input {
  border: 1px solid #c2d8d2;
  border-radius: 8px;
  padding: 9px 10px;
  font-size: 0.92rem;
  color: #274137;
}

.profile-error {
  margin: 10px 0 0;
  padding: 0 20px 12px;
  color: #c0392b;
  font-size: 0.84rem;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.profile-success {
  margin: 14px 0 0;
  padding: 0 20px 12px;
  color: #1e7a5f;
  font-size: 0.9rem;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.income-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}

.security-actions {
  padding: 0 20px 16px;
  display: flex;
  justify-content: flex-end;
}

.income-save-btn {
  border: 1px solid #2f7a6c;
  background: #2f7a6c;
  color: #ffffff;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
}

.income-save-btn:hover {
  background: #236a5d;
}

.fp-card-save-btn {
  border: 1px solid #2f7a6c;
  background: #2f7a6c;
  color: #ffffff;
  border-radius: 8px;
  padding: 5px 16px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}

.fp-card-save-btn:hover {
  background: #236a5d;
}

.fp-card-save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* AUSTRALIA MAP */
.balance-map-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.balance-map-col {
  flex: 1;
  min-width: 0;
}

.balance-sheet-col {
  flex: 0.9;
}

.portfolio-map-col {
  flex: 1.1;
}

.au-map-card {
  background: #ffffff;
  border: 1px solid #d2e3de;
  border-radius: 14px;
  padding: 12px;
}

.au-map-wrap {
  position: relative;
  border-radius: 12px;
  background: #edf5f3;
  border: 1px solid #d2e3de;
  overflow: hidden;
  min-height: 520px;
}

.au-map-canvas {
  width: 100%;
  height: 520px;
  display: block;
}

.au-map-empty-inline {
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7b918b;
  font-size: 0.92rem;
}

.au-map-addresses {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.au-map-address-label {
  font-size: 0.8rem;
  color: #5e7972;
  font-weight: 700;
}

.au-map-address-chip {
  border: 1px solid #c9ddd7;
  background: #f6fbf9;
  color: #2f4c45;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.75rem;
  line-height: 1.3;
}

::v-deep .map-flag-icon-wrap {
  background: transparent;
  border: none;
}

::v-deep .map-flag-marker {
  width: 20px;
  height: 26px;
}

::v-deep .map-flag-marker .map-flag-pole {
  position: absolute;
  left: 5px;
  top: 2px;
  width: 2px;
  height: 20px;
  background: #1f5147;
  border-radius: 1px;
}

::v-deep .map-flag-marker .map-flag-pennant {
  position: absolute;
  left: 7px;
  top: 3px;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 10px solid #2f7a6c;
  filter: drop-shadow(0 1px 1px rgba(14, 41, 36, 0.2));
}

::v-deep .map-flag-marker.is-sold .map-flag-pennant {
  border-left-color: #c93a35;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  background: rgba(8, 20, 18, 0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
}

.dialog-card {
  width: min(420px, 100%);
  background: #ffffff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.24);
}

.property-dialog-card {
  width: min(620px, 100%);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.dialog-field {
  display: flex;
  flex-direction: column;
}

.dialog-field-full {
  grid-column: 1 / -1;
}

.dialog-card h3 {
  margin: 0;
  color: #1e4c40;
}

.dialog-sub {
  margin: 8px 0 14px;
  color: #5e7972;
  font-size: 0.86rem;
}

.dialog-label {
  display: block;
  margin-bottom: 6px;
  color: #2f4c45;
  font-size: 0.84rem;
  font-weight: 600;
}

.required-star {
  color: #c0392b;
}

.dialog-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #bfd7d1;
  border-radius: 8px;
  padding: 9px 10px;
  font-size: 0.95rem;
}

.dialog-input-readonly {
  background: #f4f9f7;
  color: #7b918b;
  cursor: default;
}

.dialog-error {
  margin: 10px 0 0;
  color: #c0392b;
  font-size: 0.84rem;
}

.dialog-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.dialog-btn {
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 700;
  cursor: pointer;
}

.dialog-cancel {
  border: 1px solid #c5d8d3;
  background: #ffffff;
  color: #486861;
}

.dialog-confirm {
  border: 1px solid #2f7a6c;
  background: #2f7a6c;
  color: #ffffff;
}

.delete-confirm-card {
  width: min(460px, 100%);
  border: 1px solid #efd2cb;
  background: linear-gradient(180deg, #ffffff 0%, #fff8f6 100%);
}

.delete-summary-row {
  margin-top: 6px;
  margin-bottom: 6px;
  border: 1px solid #f2d9d2;
  background: #fffdfc;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.delete-summary-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8c5d52;
  font-weight: 700;
}

.delete-summary-value {
  color: #6c3e34;
  font-weight: 700;
  text-align: right;
}

.dialog-danger {
  border: 1px solid #bf3a2f;
  background: linear-gradient(135deg, #d14a3a 0%, #b63126 100%);
  color: #ffffff;
}

.dialog-danger:hover {
  filter: brightness(1.04);
}

.dialog-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* COMING SOON */
.soon-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 24px;
  border: 2px dashed #c4d9d3;
  border-radius: 18px;
  color: #7b918b;
  text-align: center;
}

/* RESEARCH DOCS */
.research-category-select {
  min-width: 190px;
  border: 1px solid #c2d8d2;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.86rem;
  color: #274137;
  background: #ffffff;
}

.research-category-select:focus {
  outline: none;
  border-color: #2f7a6c;
  box-shadow: 0 0 0 3px rgba(47, 122, 108, 0.12);
}

.research-doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.research-doc-card {
  background: #fffdf9;
  border: 1px solid #d2e6e0;
  border-radius: 14px;
  padding: 12px;
  overflow: hidden;
  display: grid;
  gap: 8px;
}

.research-doc-cover {
  border-radius: 10px;
  overflow: hidden;
  background: #edf4f1;
  margin-bottom: 4px;
}

.research-doc-cover img {
  width: 100%;
  height: 138px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.research-doc-placeholder {
  font-size: 3rem;
  color: #a8c8be;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 138px;
}

.research-doc-body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.research-doc-category {
  display: inline-flex;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  color: #3f8a79;
  background: #e7f4f0;
  border: 1px solid #c5e1d9;
  margin: 0 0 8px;
}

.research-doc-title {
  margin: 0 0 6px;
  color: #2f5f55;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.4;
}

.research-doc-summary {
  margin: 0 0 6px;
  color: #5c7971;
  line-height: 1.6;
  font-size: 0.88rem;
  flex: 1;
}

.research-doc-meta {
  margin: 0 0 10px;
  color: #5e9b8d;
  font-size: 0.82rem;
  letter-spacing: 0.03em;
}

.research-doc-view-btn {
  margin-top: 6px;
  border: none;
  border-radius: 10px;
  padding: 10px 14px;
  background: #4e907f;
  color: #fff;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.18s;
  text-align: center;
}

.research-doc-view-btn:hover {
  background: #3a7060;
}

/* PDF VIEWER MODAL */
.doc-viewer-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.doc-viewer-card {
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 960px;
  height: 90vh;
}

.doc-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #d2e3de;
  flex-shrink: 0;
}

.doc-viewer-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a2e28;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-viewer-close {
  background: none;
  border: none;
  font-size: 1.1rem;
  color: #5a7268;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  line-height: 1;
  flex-shrink: 0;
}

.doc-viewer-close:hover {
  background: #edf5f3;
  color: #1a2e28;
}

.doc-viewer-frame-wrap {
  position: relative;
  flex: 1;
  overflow: hidden;
}

.doc-viewer-frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.doc-viewer-popout-cover {
  position: absolute;
  top: 0;
  right: 0;
  width: 72px;
  height: 52px;
  background: #fff;
  z-index: 10;
  pointer-events: all;
}

.research-doc-unavailable {
  font-size: 0.8rem;
  color: #a0b8b1;
  font-style: italic;
}

.soon-icon {
  font-size: 3.2rem;
  margin-bottom: 16px;
  color: #a8c8be;
}

.soon-wrap p {
  margin: 0;
  font-size: 1.05rem;
}

/* MOBILE */
@media (max-width: 768px) {
  .dashboard {
    flex-direction: column;
  }

  .portal-sidebar {
    width: 100%;
    height: auto;
    position: static;
    flex-direction: row;
    align-items: center;
    overflow-x: auto;
  }

  .portal-sidebar.is-collapsed {
    width: 100%;
  }

  .sidebar-brand {
    padding: 10px 14px;
    border-bottom: none;
    border-right: 1px solid rgba(160, 210, 198, 0.14);
    min-height: auto;
  }

  .sidebar-nav {
    flex-direction: row;
    padding: 8px 10px;
    gap: 2px;
    flex: 1;
  }

  .sidebar-link {
    flex-direction: column;
    gap: 3px;
    padding: 7px 10px;
    font-size: 0.74rem;
    min-width: 52px;
    justify-content: center;
    text-align: center;
  }

  .sidebar-bottom {
    padding: 8px 10px;
    border-top: none;
    border-left: 1px solid rgba(160, 210, 198, 0.14);
    flex-direction: row;
    align-items: center;
  }

  .collapse-hint {
    display: none;
  }

  .portal-main {
    min-height: auto;
  }

  .content {
    padding: 18px 16px;
    gap: 26px;
  }

  .dash-header {
    padding: 16px 16px 12px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .balance-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cashflow-card {
    max-width: 100%;
  }

  .prop-metrics {
    gap: 12px 16px;
  }

  .pm {
    min-width: 52px;
  }

  .balance-map-row {
    flex-direction: column;
    width: 100%;
    max-width: 100%;
  }

  .balance-map-col {
    width: 100%;
    max-width: 100%;
  }

  .au-map-card {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 10px;
  }

  .au-map-wrap {
    width: 100%;
    max-width: 100%;
    min-height: 300px;
  }

  .au-map-canvas {
    width: 100%;
    max-width: 100%;
    height: 300px;
  }

  .cashflow-summary-grid {
    grid-template-columns: 1fr;
  }

  .fp-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .security-form-grid {
    grid-template-columns: 1fr;
  }

  .income-grid {
    grid-template-columns: 1fr;
  }

  .dialog-grid {
    grid-template-columns: 1fr;
  }
}
</style>
